import React, { useState, useEffect, useMemo, useRef } from "react";
import { BarChart, Bar, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useAnalytics } from "../hooks/useAnalytics";
import { useSocial } from "../hooks/useSocial";
import { useTwitter } from "../hooks/useTwitter";
import { SocialOverviewGrid } from "./social/SocialOverviewGrid";
import { GoogleAuthButton } from "./auth/GoogleAuthButton";
import { TrendingPanel } from "./trending/TrendingPanel";
import { setAccessToken, getAccessToken } from "../hooks/useAuth";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { AdminSection } from "./admin/AdminSection";
import { AlertBell } from "./alerts/AlertBell";
import { GoalsPage } from "./goals/GoalsPage";
import { ReportsPage } from "./reports/ReportsPage";
import { ShareModal } from "./share/ShareModal";
import { SharedView } from "./share/SharedView";
import { TotalSocialFollowersCard } from "./social/TotalSocialFollowersCard";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

// ── Theme System ──────────────────────────────────────────────────────────────
const THEMES = {
  dark: {
    bg: "#0a0e1a", surface: "#111827", surface2: "#1a2035", surface3: "#2d3748",
    border: "#2d3748", border2: "#374151", text: "#ffffff", textSub: "#94a3b8",
    textMuted: "#64748b", textFaint: "#475569",
    gradBg: "radial-gradient(ellipse at 20% 50%, #1e3a5f22 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, #3b1f6322 0%, transparent 60%)",
    cardGrad: "linear-gradient(145deg, #1a2035, #111827)", topbar: "#111827", name: "dark"
  },
  light: {
    bg: "#f8fafc", surface: "#ffffff", surface2: "#f1f5f9", surface3: "#e2e8f0",
    border: "#e2e8f0", border2: "#cbd5e1", text: "#0f172a", textSub: "#475569",
    textMuted: "#64748b", textFaint: "#94a3b8",
    gradBg: "radial-gradient(ellipse at 20% 50%, #dbeafe44 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, #ede9fe44 0%, transparent 60%)",
    cardGrad: "linear-gradient(145deg, #ffffff, #f8fafc)", topbar: "#ffffff", name: "light"
  }
};

// ── Constants ─────────────────────────────────────────────────────────────────
const PLATFORMS = ["NewsDay", "ZimInd", "Standard", "SouthernEye", "Hstv"];
const PLATFORM_COLORS = {
  NewsDay: "#4DA3FF", ZimInd: "#B07CFF", Standard: "#2DD4BF",
  SouthernEye: "#FFC857", Hstv: "#DC2626", ICWT: "#D97706",
};

// ── Hourly + Weekly chart helpers ─────────────────────────────────────────────
// Formats an hour-of-day (0-23) into the chart's tick label — real per-hour
// values now come from GA4's "hour" dimension (see gaHourlyViewsPerPub).
function hourLabel(h) {
  return h === 0 ? "12am" : h < 12 ? `${h}am` : h === 12 ? "12pm" : `${h - 12}pm`;
}
// Builds the {hour,label,views}[] shape HourlyReadershipChart expects from a
// real 24-value GA4 hourly breakdown.
function toHourlyChartData(hourlyValues) {
  return (hourlyValues || []).map((views, h) => ({ hour: h, label: hourLabel(h), views }));
}
// Builds the {day,thisWeek,lastWeek}[] shape WeeklyCompareChart expects from
// real GA4 daily breakdowns (current + previous 7-day window).
function toWeeklyCompareData(days, prevDays) {
  return (days || []).map((d, i) => ({ day: d.dayLabel, thisWeek: d.views, lastWeek: prevDays?.[i]?.views ?? 0 }));
}

function HourlyReadershipChart({ hourlyData, color = "#2563EB", height = 160 }) {
  if (!hourlyData || !hourlyData.length) return null;
  const maxIdx = hourlyData.reduce((mi, d, i) => d.views > hourlyData[mi].views ? i : mi, 0);
  const barFill = (hour, i) => {
    if (i === maxIdx) return "#ef4444";
    if (hour <= 5 || hour >= 22) return "#374151";
    if (hour >= 6 && hour <= 8) return "#22c55e";
    if (hour >= 18 && hour <= 21) return "#22c55e";
    return color;
  };
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={hourlyData} margin={{ top: 2, right: 4, bottom: 0, left: -10 }} barCategoryGap="20%">
        <CartesianGrid vertical={false} stroke="#94a3b8" strokeOpacity={0.15} />
        <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#64748b" }} interval={2} axisLine={false} tickLine={false} />
        <YAxis tickFormatter={v => v >= 1000 ? `${Math.round(v / 1000)}K` : `${v}`} tick={{ fontSize: 9, fill: "#64748b" }} axisLine={false} tickLine={false} width={28} />
        <Tooltip formatter={v => [v.toLocaleString(), "Views"]} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
        <Bar dataKey="views" radius={[2, 2, 0, 0]}>
          {hourlyData.map((entry, i) => (
            <Cell key={i} fill={barFill(entry.hour, i)} opacity={0.85} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function WeeklyCompareChart({ weeklyData, color = "#2563EB", height = 140 }) {
  if (!weeklyData || !weeklyData.length) return null;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={weeklyData} margin={{ top: 4, right: 4, bottom: 0, left: -10 }}>
        <CartesianGrid vertical={false} stroke="#94a3b8" strokeOpacity={0.15} strokeDasharray="3 3" />
        <XAxis dataKey="day" tick={{ fontSize: 9, fill: "#64748b" }} axisLine={false} tickLine={false} />
        <Tooltip formatter={(v, n) => [v.toLocaleString(), n === "thisWeek" ? "This week" : "Last week"]} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
        <Line type="monotone" dataKey="thisWeek" stroke={color} strokeWidth={2.5} dot={{ fill: color, r: 3, strokeWidth: 0 }} activeDot={{ r: 5 }} name="thisWeek" />
        <Line type="monotone" dataKey="lastWeek" stroke="#64748b" strokeWidth={1.5} strokeDasharray="5 4" dot={false} name="lastWeek" />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ── Hooks ─────────────────────────────────────────────────────────────────────
function useResponsive() {
  const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return { isMobile: width < 640, isTablet: width < 1024, isDesktop: width >= 1024, width };
}

function Sparkline({ data = [], color = "#2563EB", height = 44, width = 140 }) {
  if (!data.length) return null;
  const max = Math.max(...data); const min = Math.min(...data); const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 8) - 4;
    return `${x},${y}`;
  }).join(" ");
  const gradId = `sg-${color.replace("#", "")}`;
  return (
    <svg width={width} height={height} style={{ overflow: "visible" }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline points={`0,${height} ${pts} ${width},${height}`} fill={`url(#${gradId})`} stroke="none" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MiniBar({ label, value, max, color, theme: t }) {
  const pct = max > 0 ? (typeof value === "number" ? (value / max) * 100 : (parseFloat(value) / max) * 100) : 0;
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: t.textMuted, marginBottom: 3 }}>
        <span>{label}</span>
        <span style={{ color: t.textSub, fontWeight: 700 }}>{typeof value === "number" ? value.toLocaleString() : value}</span>
      </div>
      <div style={{ background: t.surface2, borderRadius: 4, height: 6, overflow: "hidden" }}>
        <div style={{ width: `${Math.min(pct, 100)}%`, height: "100%", background: `linear-gradient(90deg, ${color}, ${color}bb)`, borderRadius: 4, transition: "width 1s cubic-bezier(.4,0,.2,1)" }} />
      </div>
    </div>
  );
}

function DonutChart({ data = {}, colors, theme: t }) {
  const total = Object.values(data || {}).reduce((a, b) => a + b, 0);
  if (total === 0) return <div style={{ fontSize: 12, color: t.textMuted, padding: "10px 0" }}>No data</div>;
  let cumulative = 0;
  const segments = Object.entries(data || {}).map(([key, val]) => {
    const pct = (val / total) * 100; const offset = cumulative; cumulative += pct;
    return { key, val, pct, offset };
  });
  const CIRC = 2 * Math.PI * 30;
  const segColors = colors || ["#2563EB", "#7C3AED", "#0891B2", "#059669", "#DC2626"];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <svg width="80" height="80" viewBox="0 0 80 80" style={{ flexShrink: 0 }}>
        {segments.map((s, i) => (
          <circle key={s.key} cx="40" cy="40" r="30" fill="none"
            stroke={segColors[i % segColors.length]} strokeWidth="13"
            strokeDasharray={`${(s.pct / 100) * CIRC} ${CIRC}`}
            strokeDashoffset={-((s.offset / 100) * CIRC)}
            transform="rotate(-90 40 40)" style={{ transition: "all 0.8s ease" }} />
        ))}
        <circle cx="40" cy="40" r="22" fill={t.surface} />
        <text x="40" y="44" textAnchor="middle" fill={t.textSub} fontSize="8" fontWeight="700">{total.toLocaleString()}</text>
      </svg>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
        {segments.map((s, i) => (
          <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10 }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: segColors[i % segColors.length], flexShrink: 0 }} />
            <span style={{ flex: 1, color: t.textSub }}>{s.key}</span>
            <span style={{ color: t.text, fontWeight: 700 }}>{s.pct.toFixed(0)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars -- kept for a future activity-feed panel
function ActivityFeed({ events, theme: t }) {
  const items = Array.isArray(events) ? events : [];
  return (
    <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 18, padding: 20, height: 280, overflowY: "auto" }}>
      <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 12, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>⚡ Live Activity</div>
      {items.length > 0 ? items.map((e, i) => (
        <div key={i} style={{ fontSize: 12, color: i === 0 ? t.text : t.textSub, marginBottom: 8, padding: "7px 10px", background: i === 0 ? "#22c55e11" : "transparent", borderRadius: 8, borderLeft: i === 0 ? "2px solid #22c55e" : `2px solid transparent`, transition: "all 0.3s ease" }}>
          <span style={{ marginRight: 6 }}>{i === 0 ? "🟢" : "⚪"}</span>{e}
        </div>
      )) : (
        <div style={{ fontSize: 12, color: t.textFaint, fontStyle: "italic", padding: "20px 10px", textAlign: "center" }}>
          No live activity — GA4 realtime not configured.
        </div>
      )}
    </div>
  );
}


function PlatformCard({ platform, gaOverviewPerPub = null, gaAudiencePerPub = null, gaRealtimePerPub = null, gaChannelsPerPub = null, gaDailyViewsPerPub = null, gaHourlyViewsPerPub = null, perPubLoaded = false, theme: t }) {
  const color = PLATFORM_COLORS[platform];
  const [hovered, setHovered] = useState(false);
  const ga = gaOverviewPerPub;
  // Loading = the per-pub fetch hasn't resolved even once yet.
  // Disconnected = the fetch resolved but this publication has no GA4 property configured
  // (key absent entirely) or is explicitly flagged connected:false (e.g. SouthernEye).
  const isLoading = !perPubLoaded;
  const isDisconnected = perPubLoaded && (!ga || ga.connected === false);
  const noDataMsg = isDisconnected ? "No data — GA4 not connected" : "Loading…";

  const countries = gaAudiencePerPub?.countries || [];
  const daily = gaDailyViewsPerPub;
  const hourly = gaHourlyViewsPerPub;
  const hasWeeklyData = !isLoading && !isDisconnected && daily?.days?.length > 0;
  const hasHourlyData = !isLoading && !isDisconnected && hourly?.hourly?.length > 0;

  const trafficSources = Object.fromEntries(
    Object.entries(gaChannelsPerPub?.channels || {}).map(([name, c]) => [name, c.users || 0])
  );

  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ background: t.cardGrad, borderRadius: 20, padding: 22, border: `1px solid ${hovered ? color + "55" : color + "22"}`, boxShadow: hovered ? `0 10px 44px ${color}2a` : `0 2px 12px ${color}0e`, position: "relative", overflow: "hidden", transition: "all 0.35s cubic-bezier(.4,0,.2,1)", transform: hovered ? "translateY(-4px)" : "translateY(0)" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${color}, ${color}44)` }} />
      <div style={{ position: "absolute", top: -50, right: -50, width: 130, height: 130, borderRadius: "50%", background: `${color}08` }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
        <div>
          <h3 style={{ margin: 0, color: t.text, fontSize: 17, fontWeight: 900, letterSpacing: "-0.4px", fontFamily: "Georgia, serif" }}>{platform}</h3>
          <span style={{ fontSize: 10, color, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.5 }}>E-Paper · News</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
          {/* "Active Users" is exclusively the GA4 Realtime figure — never a historical total */}
          <div style={{ fontFamily: "Georgia, serif", fontWeight: 900, fontSize: 22, color: isDisconnected ? t.textFaint : color, lineHeight: 1 }}>
            {isDisconnected ? "0" : (gaRealtimePerPub?.activeNow ?? "—").toLocaleString?.() ?? "—"}
          </div>
          <div style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8 }}>Active Users</div>
          {isDisconnected
            ? <span style={{ fontSize: 9, color: "#94a3b8", background: "#94a3b811", padding: "2px 7px", borderRadius: 20, border: "1px solid #94a3b833" }}>Not connected to GA4</span>
            : isLoading
              ? <span style={{ fontSize: 9, color: t.textMuted, background: t.surface2, padding: "2px 7px", borderRadius: 20, border: `1px solid ${t.border}` }}>Loading…</span>
              : <span style={{ fontSize: 9, color: "#22c55e", background: "#22c55e11", padding: "2px 7px", borderRadius: 20, border: "1px solid #22c55e33" }}>● GA4</span>
          }
          {!isLoading && ga && (
            <div style={{ fontSize: 9, color: t.textMuted, marginTop: 1 }}>
              {(ga.totalUsers || 0).toLocaleString()} total users
            </div>
          )}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 16 }}>
        {[
          { label: "New Users", value: isLoading ? "—" : (ga?.newUsers ?? 0).toLocaleString() },
          { label: "Sessions", value: isLoading ? "—" : (ga?.sessions ?? 0).toLocaleString() },
          { label: "Engagement", value: isLoading ? "—" : (ga?.engagementRate || "—") },
        ].map((m, i) => (
          <div key={i} style={{ background: t.surface2, borderRadius: 10, padding: "10px 8px", textAlign: "center", border: `1px solid ${t.border}` }}>
            <div style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 3 }}>{m.label}</div>
            <div style={{ fontSize: 16, fontWeight: 900, color, lineHeight: 1, fontFamily: "Georgia, serif" }}>{m.value}</div>
          </div>
        ))}
      </div>
      {!isLoading && ga && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginBottom: 16 }}>
          {[
            { label: "Avg Duration", value: ga.avgSessionDuration || "—", c: color },
            { label: "Bounce Rate", value: ga.bounceRate || "—", c: t.textMuted },
            { label: "Returning", value: (ga.returningUsers || 0).toLocaleString(), c: color },
          ].map((s, i) => (
            <div key={i} style={{ background: t.surface2, borderRadius: 8, padding: "8px", textAlign: "center", border: `1px solid ${t.border}` }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: s.c }}>{s.value}</div>
              <div style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.7, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}
      <div style={{ marginBottom: 16, padding: "12px 14px", background: t.surface2, borderRadius: 10, border: `1px solid ${t.border}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8 }}>Weekly Views</span>
          {hasWeeklyData && <span style={{ fontSize: 13, color, fontWeight: 800 }}>{daily.total.toLocaleString()}</span>}
        </div>
        {hasWeeklyData ? (
          <>
            <Sparkline data={daily.days.map(dd => dd.views)} color={color} width={200} />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
              {daily.days.map((dd, i) => (
                <span key={i} style={{ fontSize: 9, color: dd.isToday ? color : t.textFaint, fontWeight: dd.isToday ? 900 : 400, textAlign: "center", minWidth: 14, lineHeight: 1 }}>
                  {dd.dayLabel}
                </span>
              ))}
            </div>
          </>
        ) : (
          <div style={{ fontSize: 12, color: t.textFaint, fontStyle: "italic", padding: "8px 0" }}>{noDataMsg}</div>
        )}
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700, marginBottom: 8 }}>Hourly Readership — Today</div>
        {hasHourlyData ? (
          <HourlyReadershipChart hourlyData={toHourlyChartData(hourly.hourly)} color={color} height={110} />
        ) : (
          <div style={{ fontSize: 12, color: t.textFaint, fontStyle: "italic", padding: "8px 0" }}>{noDataMsg}</div>
        )}
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700, marginBottom: 4 }}>
            This Week vs Last Week
            {hasWeeklyData && (
              <span style={{ marginLeft: 10, fontWeight: 400, letterSpacing: 0, textTransform: "none" }}>
                <span style={{ fontSize: 9, color, marginRight: 8 }}>— this week</span>
                <span style={{ fontSize: 9, color: "#64748b" }}>- - last week</span>
              </span>
            )}
          </div>
          {hasWeeklyData ? (
            <WeeklyCompareChart weeklyData={toWeeklyCompareData(daily.days, daily.prevDays)} color={color} height={110} />
          ) : (
            <div style={{ fontSize: 12, color: t.textFaint, fontStyle: "italic", padding: "8px 0" }}>{noDataMsg}</div>
          )}
        </div>
      </div>
      {countries.length > 0 ? (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, fontWeight: 700 }}>🌍 Top Countries</div>
          {countries.slice(0, 4).map(c => (
            <MiniBar key={c.country} label={c.country} value={c.users} max={(countries[0] || {}).users || 1} color={color} theme={t} />
          ))}
        </div>
      ) : isDisconnected ? (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, fontWeight: 700 }}>🌍 Top Countries</div>
          <div style={{ fontSize: 12, color: t.textFaint, fontStyle: "italic" }}>No data — GA4 not connected</div>
        </div>
      ) : null}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, fontWeight: 700 }}>🔗 Traffic Sources</div>
        {isDisconnected
          ? <div style={{ fontSize: 12, color: t.textFaint, fontStyle: "italic" }}>No data — GA4 not connected</div>
          : <DonutChart data={trafficSources} colors={["#2563EB", "#7C3AED", "#0891B2", "#059669"]} theme={t} />
        }
      </div>
    </div>
  );
}

function AcquisitionChannelTable({ isMobile, theme: t, channels = [], totals = {} }) {
  if (channels.length === 0) {
    return (
      <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: isMobile ? 16 : 24 }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 14, color: t.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>User Acquisition by Channel</h3>
        <div style={{ fontSize: 13, color: t.textFaint, fontStyle: "italic", padding: "20px 0" }}>No GA4 channel data available — check GA4 connection.</div>
      </div>
    );
  }
  return (
    <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: isMobile ? 16 : 24, overflowX: "auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 8 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14, color: t.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>User Acquisition by Channel</h3>
          <p style={{ margin: "4px 0 0", fontSize: 11, color: t.textFaint }}>GA4 · aggregated across all publications</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, background: t.surface2, borderRadius: 8, padding: "5px 12px", border: `1px solid ${t.border}` }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e", display: "inline-block" }} />
          <span style={{ fontSize: 10, color: t.textMuted }}>{(totals.totalUsers || 0).toLocaleString()} total users</span>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {channels.map(ch => (
          <div key={ch.channel}
            style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, transition: "background 0.15s" }}
            onMouseEnter={e => e.currentTarget.style.background = t.surface2}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            <div style={{ width: 8, height: 8, borderRadius: 2, background: ch.color, flexShrink: 0 }} />
            <span style={{ flex: 1, fontSize: 13, color: t.text, fontWeight: 800 }}>{ch.channel}</span>
            <div style={{ background: t.surface3 || t.surface2, borderRadius: 6, height: 6, width: isMobile ? 80 : 160, overflow: "hidden", flexShrink: 0 }}>
              <div style={{ width: `${ch.totalUsersPct}%`, height: "100%", background: ch.color, borderRadius: 6 }} />
            </div>
            <span style={{ fontSize: 12, color: ch.color, fontWeight: 900, minWidth: 60, textAlign: "right" }}>{ch.totalUsers.toLocaleString()}</span>
            <span style={{ fontSize: 10, color: t.textFaint, minWidth: 34, textAlign: "right" }}>{ch.totalUsersPct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChannelEngagementCard({ isMobile, theme: t, gaOverview }) {
  const metrics = [
    { label: "Engagement Rate", value: gaOverview?.engagementRate ?? null, color: "#2563EB" },
    { label: "Avg Session Duration", value: gaOverview?.avgSessionDuration ?? null, color: "#7C3AED" },
    { label: "Bounce Rate", value: gaOverview?.bounceRate ?? null, color: "#0891B2" },
    { label: "Sessions / User", value: gaOverview?.sessionsPerUser != null ? gaOverview.sessionsPerUser.toFixed(2) : null, color: "#059669" },
  ];
  return (
    <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: isMobile ? 16 : 24 }}>
      <h3 style={{ margin: "0 0 16px", fontSize: 13, color: t.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Overall Engagement</h3>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {metrics.map(m => (
          <div key={m.label} style={{ background: t.surface2, borderRadius: 12, padding: "14px", border: `1px solid ${t.border}` }}>
            <div style={{ fontSize: m.value === null ? 11 : 20, fontWeight: 900, color: m.value === null ? t.textFaint : m.color, fontFamily: "Georgia, serif", fontStyle: m.value === null ? "italic" : "normal", marginBottom: 4 }}>
              {m.value ?? "No data"}
            </div>
            <div style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.7 }}>{m.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Trending Stories Panel — real GA4 top pages ───────────────────────────────
function TrendingStoriesPanel({ isMobile, theme: t, pages = [] }) {
  if (pages.length === 0) {
    return (
      <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: isMobile ? 16 : 24 }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 14, color: t.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>🔥 Top Pages — Last 7 Days</h3>
        <div style={{ fontSize: 13, color: t.textFaint, fontStyle: "italic", padding: "20px 0" }}>No page data — connect GA4 to see top articles here.</div>
      </div>
    );
  }
  return (
    <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: isMobile ? 16 : 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14, color: t.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>🔥 Top Pages — Last 7 Days</h3>
          <p style={{ margin: "4px 0 0", fontSize: 11, color: t.textFaint }}>GA4 · showing top 10 of {pages.length} pages</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, background: t.surface2, borderRadius: 8, padding: "5px 12px", border: `1px solid ${t.border}` }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e", display: "inline-block" }} />
          <span style={{ fontSize: 10, color: t.textMuted }}>GA4 Live</span>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {pages.slice(0, 10).map((page) => {
          const color = "#2563EB";
          return (
          <div key={page.rank}
            style={{ padding: "14px 16px", background: t.surface2, borderRadius: 12, border: `1px solid ${t.border}`, transition: "all 0.2s", cursor: "default" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = "#2563EB44"; e.currentTarget.style.transform = "translateX(3px)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = t.border; e.currentTarget.style.transform = "translateX(0)"; }}>

            {/* Top row: rank + title + views */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: `${color}1a`, border: `1.5px solid ${color}44`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <span style={{ fontSize: 12, color, fontWeight: 900 }}>#{page.rank}</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, color: t.text, fontWeight: 700, lineHeight: 1.4, marginBottom: 4, fontFamily: "Georgia, serif" }}>{page.title}</div>
                <div style={{ fontSize: 11, color: t.textFaint }}>{page.hostname}</div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontSize: 15, color: "#2563EB", fontWeight: 900 }}>{page.views.toLocaleString()}</div>
                <div style={{ fontSize: 9, color: t.textFaint, textTransform: "uppercase", letterSpacing: 0.7, marginTop: 2 }}>views</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 14, marginTop: 8 }}>
              <span style={{ fontSize: 10, color: t.textMuted }}>Engagement: <strong style={{ color: "#2563EB" }}>{page.engagementRate}</strong></span>
              <span style={{ fontSize: 10, color: t.textMuted }}>Bounce: <strong style={{ color: t.textSub }}>{page.bounceRate}</strong></span>
            </div>
          </div>
        );
        })}
      </div>
    </div>
  );
}


function AgeDemographicsPanel({ isMobile, theme: t, gaData = {}, gaConnected = false }) {
  const [selectedPub, setSelectedPub] = useState("all");

  const pubOptions = [
    { key: "all",      label: "All Platforms", color: "#4DA3FF" },
    { key: "newsday",  label: "NewsDay",        color: "#4DA3FF" },
    { key: "zimind",   label: "ZimInd",         color: "#B07CFF" },
    { key: "standard", label: "Standard",       color: "#2DD4BF" },
  ];

  const selectedData = selectedPub === "all"
    ? (gaData.audience || null)
    : (gaData.audiencePerPub?.[selectedPub] || null);

  const countries = selectedPub === "all"
    ? (gaData.audience?.countries || [])
    : (gaData.audiencePerPub?.[selectedPub]?.countries || []);

  const hasData = !!(selectedData && selectedData.totalUsers > 0);
  const pubColor = pubOptions.find(p => p.key === selectedPub)?.color || "#4DA3FF";

  const newReturnData = hasData ? {
    "New": selectedData.newUsers || 0,
    "Returning": Math.max(0, (selectedData.returningUsers || 0)),
  } : {};

  const statusBadge = gaConnected
    ? hasData
      ? <span style={{ fontSize: 10, color: "#22c55e", background: "#22c55e11", padding: "3px 10px", borderRadius: 20, border: "1px solid #22c55e33", fontWeight: 700 }}>● GA4 Live</span>
      : <span style={{ fontSize: 10, color: "#f59e0b", background: "#f59e0b11", padding: "3px 10px", borderRadius: 20, border: "1px solid #f59e0b33" }}>No data available</span>
    : <span style={{ fontSize: 10, color: t.textFaint, background: t.surface2, padding: "3px 10px", borderRadius: 20, border: `1px solid ${t.border}` }}>GA4 Offline</span>;

  return (
    <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: isMobile ? 16 : 24 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 14, color: t.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>👤 Audience Demographics</h3>
        {statusBadge}
      </div>

      {/* Publication filter */}
      <div style={{ display: "flex", gap: 6, marginBottom: 20, flexWrap: "wrap" }}>
        {pubOptions.map(p => (
          <button key={p.key} onClick={() => setSelectedPub(p.key)}
            style={{ background: selectedPub === p.key ? p.color : t.surface, border: `1px solid ${selectedPub === p.key ? p.color : t.border}`, borderRadius: 8, padding: "5px 13px", cursor: "pointer", fontSize: 11, fontWeight: selectedPub === p.key ? 800 : 600, color: selectedPub === p.key ? "#fff" : t.textMuted, transition: "all 0.18s", fontFamily: "Georgia, serif" }}>
            {p.label}
          </button>
        ))}
      </div>

      {!gaConnected ? (
        <div style={{ background: t.surface2, border: `1px solid ${t.border}`, borderRadius: 14, padding: "28px 24px", textAlign: "center" }}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>📊</div>
          <div style={{ fontSize: 13, color: t.textMuted, fontStyle: "italic" }}>Connect GA4 to see audience breakdown and location data.</div>
        </div>
      ) : !hasData ? (
        <div style={{ background: t.surface2, border: `1px solid ${t.border}`, borderRadius: 14, padding: "28px 24px", textAlign: "center" }}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>📊</div>
          <div style={{ fontSize: 13, color: t.textMuted, fontStyle: "italic" }}>No audience data available for this selection.</div>
        </div>
      ) : (
        <div>
          {/* Summary metrics */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(3,1fr)", gap: 12, marginBottom: 20 }}>
            {[
              { label: "Total Users",     value: selectedData.totalUsers     || 0, color: pubColor },
              { label: "New Users",       value: selectedData.newUsers       || 0, color: "#22c55e" },
              { label: "Returning Users", value: selectedData.returningUsers  || 0, color: "#a78bfa" },
            ].map(m => (
              <div key={m.label} style={{ background: t.surface2, borderRadius: 12, padding: "12px 14px", border: `1px solid ${t.border}` }}>
                <div style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4, fontWeight: 700 }}>{m.label}</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: m.color, fontFamily: "Georgia, serif", lineHeight: 1 }}>{m.value.toLocaleString()}</div>
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 20 }}>
            {/* New vs Returning donut */}
            <div style={{ background: t.surface2, borderRadius: 14, padding: "16px 18px", border: `1px solid ${t.border}` }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: t.text, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 14 }}>
                New vs Returning
              </div>
              <DonutChart data={newReturnData} colors={["#22c55e", "#a78bfa"]} theme={t} />
            </div>

            {/* Top countries */}
            <div style={{ background: t.surface2, borderRadius: 14, padding: "16px 18px", border: `1px solid ${t.border}` }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: t.text, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 14 }}>
                🌍 Top Countries
              </div>
              {countries.length > 0 ? (
                countries.slice(0, 6).map(c => (
                  <MiniBar key={c.country} label={c.country} value={c.users} max={(countries[0] || {}).users || 1} color={pubColor} theme={t} />
                ))
              ) : (
                <div style={{ fontSize: 12, color: t.textFaint, fontStyle: "italic" }}>No location data available.</div>
              )}
            </div>
          </div>

          {/* Age/gender note */}
          <div style={{ marginTop: 16, background: "#f59e0b0a", border: "1px solid #f59e0b22", borderRadius: 10, padding: "10px 14px", display: "flex", alignItems: "flex-start", gap: 8 }}>
            <span style={{ fontSize: 14, flexShrink: 0 }}>ℹ️</span>
            <span style={{ fontSize: 11, color: t.textMuted, lineHeight: 1.5 }}>
              <strong style={{ color: "#f59e0b" }}>Age &amp; gender data</strong> require <strong>Google Signals</strong> to be enabled in GA4 (Admin → Data Settings → Data Collection).
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function ThemeToggle({ isDark, onToggle }) {
  return (
    <button onClick={onToggle}
      style={{ background: isDark ? "#1e293b" : "#f1f5f9", border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`, borderRadius: 10, padding: "6px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: isDark ? "#94a3b8" : "#475569", transition: "all 0.25s ease" }}>
      {isDark ? "☀️" : "🌙"}
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>{isDark ? "Light" : "Dark"}</span>
    </button>
  );
}

// ── AMH Logo as base64 (embedded) ─────────────────────────────────────────────
const AMH_LOGO_SRC = "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/4gHYSUNDX1BST0ZJTEUAAQEAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADb/2wBDAAUDBAQEAwUEBAQFBQUGBwwIBwcHBw8LCwkMEQ8SEhEPERETFhwXExQaFRERGCEYGh0dHx8fExciJCIeJBweHx7/2wBDAQUFBQcGBw4ICA4eFBEUHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh4eHh7/wAARCAETARQDASIAAhEBAxEB/8QAHQABAAIBBQEAAAAAAAAAAAAAAAcIAQIEBQYJA//EAFQQAAEDAwIDAgcKCQcJCQAAAAEAAgMEBREGBxIhMQhBExc3UVdh0hQicXR2gZGVsbQVFiMyM3OhstE0NkJScrPBGFNVVmKCkpSkJENGVIOEk6Lw/8QAGwEBAAIDAQEAAAAAAAAAAAAAAAECAwQFBgf/xAAxEQEAAQQABAQFAwMFAAAAAAAAAQIDBBEFEiExBhNBcRQyMzRRYaHBFSKxFkJTcpH/2gAMAwEAAhEDEQA/AKyIpm7Pew923MP4ZuVRJatNRvLDUtaDLUuHItiB5YHQuPIHlgnOLb6Y2M2qsFKyCDRttrXAe+luMfup7z5z4TIHzABNjziRenHiy229Hukvqan9hPFltt6PdJfU1P7CjY8x0Xpx4sttvR7pL6mp/YTxZbbej3SX1NT+wmx5jovTjxZbbej3SX1NT+wniy229Hukvqan9hNjzHRenHiy229Hukvqan9hPFltt6PdJfU1P7CbHmOi9OPFltt6PdJfU1P7CeLLbb0e6S+pqf2E2PMdF6ceLLbb0e6S+pqf2E8WW23o90l9TU/sJseY6L048WW23o90l9TU/sJ4sttvR7pL6mp/YTY8x0Xpx4sttvR7pL6mp/YTxZbbej3SX1NT+wmx5jovTjxZbbej3SX1NT+wniy229Hukvqan9hNjzHRenHiy229Hukvqan9hPFltt6PdJfU1P7CbHmOi9OPFltt6PdJfU1P7CeLLbb0e6S+pqf2E2PMdF6ceLLbb0e6S+pqf2E8WW23o90l9TU/sJseY6L048WW23o90l9TU/sJ4sttvR7pL6mp/YTY8x0Xpx4sttvR7pL6mp/YTxZbbej3SX1NT+wmx5jovTjxZbbej3SX1NT+wniy229Hukvqan9hNjzHRek192Y2svNK6nqdC2SAOGOKjphSvHrDouEqrvaE7OVZomgn1NpGee52KEF9VBLgz0jf62QMPYO84BHfkZIbFe0RFI9WLHa6CyWektFsp2U1FRwthgiYMBrWjAC3qIqgiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAtEsccsT4pWNkje0tc1wyHA9QR3ha0QebXaG0ZFoTdm8WOkZwUDnipoh/VhkHEGj1NPE3/dRSJ292tbvFayAAXWCEn1nw9QP8EVheFERVBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERBSDt8+WG0/J+H7xUInb58sNp+T8P3ioRWF30RFUEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREFIO3z5YbT8n4fvFQidvnyw2n5Pw/eKhFYXfREVQREQEREBERAREQEREBERARFg8hlO526souobo66tmgdKVF7uTxI5vvaeAfnTSdzQtps5uNbtxNJxXamb4CraA2rpD+dDJ3j4PMr8inmO9IsNIc3IWVTsvHXqIiICIiAiIgIiICIiAiIgIiIKQdvnyw2n5Pw/eKhE7fPlhtPyfh+8VCKwu+iIqgiIgIiICxlAc9FoLxz6H50GvJ9SZK08Q84+lA9vnCEQ1ZRMoFKN76MZ8+FnIPRaTgEpkedv0qdJ6M5PqW1ulbT0NBNV1LuGKJpc4/AvtLIxrC4uaAO89Aoh1XfazVWoo6G0zN/BNO/37gf0zh1+ELJatc1W2K7ciiNT3RJrqpvG5GvHy3Br2UVP7yipz0a3vdjzn/ALY6Tlvm3Ouo6y1RvfBI7wVXDjlLGf8RyOfUrK2rRtA6SOvMTGSloB97j5l8tSaWoIKOatbTNkmweo7/Utybln5Ijqw02rk9ap07bYrlDdbVTV1MeKOZmfWD5lvg4FV50pr+s0nq40V3bwWioeAfNC7pxfB51YGnnimiEsT2OY/m1wPJw84Wneszbq6s9FymvpS+uT5kz5ua0l7OhIz6ljwjR15c+oWHTJr0fTPnTI84Wnjb3Ob9KwXNyASMnzK2tmmsHKytLMY5HK1KBg9FgHz4WXYAOei05aAkQNWR50ytJc0dS36VhsjS7AKnSdTPWGsHKLAIPRZHMqER1ZREUCkHb58sNp+T8P3ioRO3z5YbT8n4fvFQisLvoiKoIiICx1Q9FggYCmD3aJHta3JOBglx9QVQd0twL5X66ubrVeq2moIZjFFHDLgENOOL5+qsTvVqL8WdAV9XFIGVErTFBz58TuhVMMlwyTknv86w3atdIeq8OYUXKartUfpDnfxy1X/R1FdAP15Xb9ntf32k3Btkd2u1XV0VTJ4CRs0nEAXAhp/4sKM1qhkkhnimidwyRuD2nzEdD9KxU1zEvS5GFbrs1Ua1uHoFE7ibkd6+gXWdsr9FqPRVsubHZdJCGyDPRzeR+zPzrsp71tx1jb5ldpmi5yy4DX8skOjbrPE90b2UzyHDqDhUuZqrU3A3GoLiOX+dKufuR/MS8/E3/AGKjEf6Nv9kLBdmez1nhq3RVRXNcb1LuOkdeXm23cG6XOurbfOww1ETpM+9d3j1hd/0hZpNO3drjdvD26UeEpJweUkZ6H4cY5KED68Y7/gXf9B3IXmyv0XWVToKriL7TMXfo5AD+SJ8zhkD1kLNh3uWrlll8QcIou2/Ntx/dCylm1dbpaRwbNx+BHM5W0q9ZUFzopoqaVvhBnAJVZLdq6qsD6ygrnOZPG4xytdyLSF1ODXNzjvwZQOkllqpRE1jBnic44aAPWeS6M27cVRXt4Cu/VM6qj9Ep6qtVZqPUbaF72xQkGSpnP5sUQ6kldd1bri5VFZDR2G6VtJaKGIU9Exspa50bRgOd6yACuT17e5LNYY9LxzCS5VAE13nac4OBiAeoDmfhUdYIAz1wCufmX+erT3Ph3g9Ni1N25HWXMfjXqjHLUFyP/rlSnuvfL1T7X6Gqqa61cM89Mx0z2SEF+Y+/z81Cilrd7ySbf/FI/wC6WrG5djMtW6cizqmPx+yPfxr1R/rDcv8AmCpF7PN+vVw3KpaWsu1ZURmGQlkkpIJwVESk3szeVOk/UyfulTRMxOluJ2LcYtUxTG4ja3MQIbg9y1rA6LK2XziWHcmlQR2q7pc7VSWY0NwqaUySuDhE8ji5FTwq99sH+T2H9a/90qtU6h0+D0xXl00z2Qp+Nep+7UFyH/uCpG7Ot/vlfuTBTV11q6qEwPJZLIXDOFD6k/syeVSD4tJ9iwUVTzbe14nZtxiV1RT6Lb4ABwFlvQLI71gLZfN/RqREUCkHb58sNp+T8P3ioRO3z5YbT8n4fvFQisLvoiKoIiIMHosO7llwyFsL9corVaKy4VBDYqeF0hJ9Qyp3qNpiia55Vb+1Vqb3dqSm09A7igoo/CygHq93d8wA+lQqOXN3It7vOuQ1FdZbxfq26zO45KmZ0mT3DOB+xfK00E10u1JQQAvkqZmQjz++IBPzZWpVMzL6ZhWqcTGpj8Rtz9x0hVUW3du1ZIHCOrqnR4I6MwOE/OeL6F1Zw5Foz1xz7/8A8FcbXuj4KvaObTsMQBpqQGEAf02jOfpyqdni5lww7zHqMpVTMNfhOd8Xaq5p6xP7LB9kvURkjuOmZn/oXCogyeoPJwHwcI+lWDVI9or+dNa+tlxdJwwcfgps/wBRx5q7Mb+NoIxz6LNRO408t4gxvJypqjtLgNyf5i3n4o/7FRiP9G3+yFefcn+Yt6+KP+xUYZ+jZ/ZCrd7ur4Y626/dnqMdy1QvfBIySFxY9hBa4dQR0IXKaRsk2otQU1np5BHLPnhJ6ZC2Fwo6u31s1DWwugqYXFkjCObXDuWKYmOsPT89NVzy57uY3Htbdb6VOsLaMXq2MEd4pmjnLEB7ydo7yBkH4AvhtVZ4NJacOvbrE19zqmmKx08n9EHk6c5/2c4+ELOkL7VadvcN0gZG7A4ZonjLJmd7XDvCay1BNqO9PrpYRSU7cR0tMxoDYIhyDQO7ksnmzy9XmqvD1uc3nmP7O7iaiWSepkqJnuklkcXPe7qSTlaMdfpX0pqeepqYqenifJLK8MYwcySei5bWmnqnTF7NorHtdUMhjfJjoHObkj5ljiJmOaXo5u0RV5frr9nCqWt3vJJt/wDFI/7pRKpa3e8km3/xSP8AulalpZv17Pv/AAiVSb2ZvKnSfqZP3SoyXadqtWQaN1fDeqmlkqWMY5ojY4AnIx3qKZ6s+fbquY1VNPeY0u+OiyoGb2kLWGji0zXZ7/yzeX7FqHaQtR/8M1//AMzf4LY56Xgf6Rmf8cp3Ve+2D/J7D+tf+6Vvf8o+1f6tVw9fhm/wUbb17kUuvorfHT2yeidSvLvyrweIEY7lWquJh0eFcNybOVTXco1EI27lJ/Zj8qkHxaT7FGHnUn9mPyqQfFpPsWGju9VxT7OuP0lbkd6wFkd6wFtPmXo1IiIKQdvnyw2n5Pw/eKhE7fPlhtPyfh+8VCKwu+iIqgiLGRnGeaAVC3am1M636VjsVPJwz3CQcYB6RtOT+0AfOpnkcAwkkcuapjvfqQal3ArJYpOOlpSaeAg55N5E/ORlY7k6h2eBYvxGTue1PV0doGOY58+nmUt9l7TouetZLxLGHwW2Iluf847l+wElRIpi2f3SsGhtMmgmtVTUVU0hfNIxwGfMOnmWGiqPV7Divmzi1W7Mbmf8LQvjD2FpHFkYKpRu7Ynae3Du1DwcMLpnTQ/2HHIHzZU1/wCUVYcjFkrwPW8fwUU7161s+ubpR3K32+opJoYzHIZHA8QzyWSquKocTgeLl4uRMV0zyzCP2cntPmOVdDZjUR1FoC21kr+OdsYimx3PbyP7QqXE4GefzKd+ybf/AHPdLjpueQBsjfDwAn+kORH0ZPzKtqrq3/EOL5mPNyO8Jv3K/mNevij/ALFRhn6NnwBXn3J56FvOP/KP+xUYZ+jZ8AVrvdqeF/p1+7u+xYzunZQRy8LlSp2mNvnVUI1dZ4MyxY92MaObm9OP4R3+rKizYvnulZW+eVXJqomTwPgnia+KRpY9ruYcCMYS3TzKcbyq8bOouU+kKASZcCD07isHGck4BHP1/CpC3u0HJozUhlpmvfZ655dA7GRG7vZn1f4r4bM6Fm1rqWMzRltspuGSpeP6Teoj+f7MrHNM82nfnPtzi/ERPaEidmjb7341hd4c45UMbh087/4fAuh9oxobu1dAO6OL91W7oqWCipY4KaERxRsDWMHINAVRe0ac7sXL9XF+6stdPLTp53hWZVl59d2e0wjtS1u95JNv/ikf90olUtbveSTb/wCKR/3Sxw7ub9ez7/wiVZaTwOy7IPcT9iwu8bH2G2aj15Ba7rTeHpnxPJZxOGSAe8FUpbmRfjHtzcns6PyHIEHHXknXv/YrhM2W28A52Ln6qiX2lqOy23fdYv8AqJPaWXy5lw/9TY3rE/8AinYaM8zkesIDhnCHY9QKuH4l9vP9Bf8AUy+0od7RmitPaQhtLrHQCkdPI5smJHuyMHzkqJpmGfG49ZyLkW6I7oeCk/sx+VSD4tJ9ijBSf2Y/KpT/ABaT7FSj5m7xTph3PZbkd6wFkd6wFtvmXo1IiIKQdvnyw2n5Pw/eKhE7fPlhtPyfh+8VCKwu+iIqgtL+oCyT1Wl56ebvUyjfrDp+8epG6Y0HcK9j+GpfGYoB53u6KlJGQA+TLyffOPefOpu7VWp/dl9o9NQOBjpGeHnwf6TjgA+sBuf95QiACefeta5309/4fx4sY/NMdav8DQe5pd/ZGVq8FIefg3/8BU6dljSUFwdcb/cKWKamBFPCyVgc0uHNxAPqLVPo07YABiy27HxVn8FMW+imb4gtY16bVNO4hQ7wUn+bd/wFPByD/u5Pmar4/i7Yf9CW3/lWfwWmXTlgLCDZbcM8v5Kz+CmLTUnxRRPy0TE+6hxBzzyPhXO7d3uTTmtLZd2Se8ina2b1td7137CSt7u3Ynae3CultLC2Lj8LDywOB3THz8S6oG4WLXLU9HTVTlWI5e1fdeHXUzKjby6TxnLJKFzmn1EZVHIv0bVafRGovxg2ArZJXcVRSUslPKM88tGR+whVZi5xtPXIWW73cTgNmqzN61PpLvWxHlWsn61XPf8AmKl+xPlWsn65XQd0wr2nK8UfcU6/DreutL0OrNPVFprAOCYe8eBkxu/rBfLbvSVHo7TlPaaTD3sHFNIRgyPPVx+ddpxz6JjJ84KyT8zhfE18nJ6MDDm5Cp72jvK1c/1UP7quGBhpVPe0f5Wrn+qh/dVL3Z2fD3TKnX4lHSlrd7ySbf8AxSP+6USqWt3vJJt/8Uj/ALpYKXqs369n3/hEqk3szeVOk/UyfulRkpN7M3lTpP1Mn7pSn5mTin2tf/WVux0WVgdFlbT5lLB6Kv3bC/k9h/XO/dKsEq+dr/8Akti/Wv8A3SqV/K6vBfvaFeT3qT+zH5VIPi0n2KMO5Sf2Y/KpB8Wk+xa9Hd7jif2dftK3I71gLI71gLafMvRqREQUg7fPlhtPyfh+8VCJ2+fLDafk/D94qEVhd9ERVGCOq+FU6RkLnRN4nhpLR5z5l9881pdku7sd+VJTOp3Cpeqtsdx73qSvus9nDpKqdzsmdvIdB+wLizs9uCWkCyhpzyzM3p9KuMMnmCFDO9d7rbpf26Vs8l6ilt0Hu6qntdPJKWzcOYYn8HQOJaTnq0FUm3udu7b8Q5FNMURTqId+2w0/+K2jKG0EATRx8Uru4vPX+HzLtIfyyeXq8yhvVms3Xrs9Vl+hqZrfXQxiKrOTE+GVpw4HPNvcceYrs+s9T2a8bWaorNOXumrHU9snzNR1IcYneDJHNp5FZNahxbtzzbk1z3l37j99jl0zhaC4kEHpnA9eVDey2ndS0HuO9T+7qO1zWppmiqrk6rdUTFuRIOJxLB6hgLqtTf8AV7NsmzniNCdSxMNcKsmcg1zBw9c4OcY8xKQpLsnaK2/vOqKq3XSwUIqamNrop28YaSwc2nn6yVEw2d3BIB/AoGeo8M3opp3svstbPQaMtct3ZNUxmqrprXC+WaCIcmj3mSOMhwz/ALJXZNmtT1GpNHxC6QzQXigd7lr4ZInMeJG8uItd74A4yMrHXa3Ls43GsjGtRRRqYhF21uiNdWCz6itVfbC2nuFIfBASNI8JggDr35/YugjZrcJreE2VoxgfpWq42A4gAch1BXRd+31bNsLiKGtnoKmSWmijqInYfGX1Ebcj5iqzbme61vjuRbqmqIjc90O7UbZa1se4FrulxtQipYJMySCVpx8ys41+eRxz7/Wq8a41VqO46HNlZUVlBcbG+P8ADFTES0vAx4MNd38Q5nC7Z2kKp8Fs02wVxpYZ7qGz8Na+mZIzwTzze1wIGcd6yUxyw0M7OrzKuevulxzwGkgghYDxnlzHnUdyzUdNslXTWityyOikc2eGufUBruecSlxJ+lRroG52iiu2h4NEaqqbzXXAj8N0rri+tDIfAucXuD3O8GeMMHLHXCs1P9qx0jyBy5n1Kt29W2+rtQbh110tds8PSysjax/hGjmG8+RKlWpq5xvnSUbaqT3M+zl5h4/eF3G733D5/Wul7y6guVx1K+2WB18AsMYrHfgykklE9UCC2CQtBw0t4sg96iqnmjTawc6rEr56I6oqOze4XDkWUE56CVqkPcfb/Vd1280haaO3tkqrfCyOoYZAOAiPHz81MugdRUuq9I0F+pgW+6I8SMJ5xvHJ7SO4hwK6VvTW3ml1rt+2yvYJprrM0xSvc2OX/ssxw4AjI5Z+EBU8p0LnG8m5VFUxG47IM8T24HMfgPmOXOZvX6V3jYvbvV2ndewXS6W5sNLHE9rpPCNIzg+Yr63C6anbatduuNZNTlt+hirXUcr8U1MYYy9zMkloweoPXK+lPW2WC56hptuNQVF0sh0xVPq3x3B9VHBOGfkyJHOcWvIzyBHeVPlQnI49lX6Jt1xEbWEZKOH3zhgZ5hZDxj84HHUhQVX64sN17Olxgs2qaWtu1JZGmp9zVodPCcYJdwnib0PP1LYaQrdPR6usMW3GpKi8000E34cYLlJWxwxiF5a4l7ncLvCBgxy6q2nD6+qwQlPcWnPTJUPdo7R+odW09pZYaUVTqeRxlPEG8IIPNdNoDqS72XbGK032tpa+SW7zB7pS5szo5hwNkB/Ob3YPTuwt6NVaousW4lVS0twoLnTR0ENTREuJpsFrah0fq4OMhw64yk07Z8bIrx7kXaO8Ogt2e1+9xbHZ2OLev5dq7xsXt7qzTe4EVxutA2GnZC9jniRpOSPMFzel79omn1bbbTpipnrH11tle65R3t9Q2MtA4uOJzzwkE9cDnnzLTsdc6Gl1PBZI7jR6gq328vfeKG6PqI5MObzewvcGuPXIACrFuIda/wAeyMi3NFcRESnZhdzJxgrUFoZgnIOe5a1Zwd7llERQlSDt8+WG0/J+H7xUInb58sNp+T8P3ioRWF30RFUY7+i2d6nkprTV1MQ9/FC97eWeYaSFvVsr5DJUWisp4hxSSU72NbnAJLSB+0qUeyo9BvZvPdrlQ0tvjshNxmmjpGvgxxeDdg5PzharRuzvTUXW4UlPBpuCtpYo5KoTwlrnF2A1ucZLuYGDhcTTbPb6UFbRz0FooiKCWZ1KfdsYID3ZPIn1Bbuk237Qcd8ud6dY7XLWXONoqJJqiE5IxhwHc4EAovuXyk3j3Xk0fU3yam0yy1e63wSxvpiXvlbji5Yx3jqVvancveS0m2ULbfpeIX4NEDWU54XBzc4fy82emVxI2h33/FOo0y+xW99BPO+odxVkXEZH4yR9ASfaDfybUFBd3W2nfPbgxtKJLjGY42sbwj3ucAYREudtm7291Vea+wW+DT09Ra42uqI4oS1rWnIGMgZ6dMLaQbubu1Gn6+4R0mljR0E7xNGaf8o57CS5zW9Dggnr3L5Ue2naBotR1WoaS00FPcKssMz2VkRDuHOOR69T+xbWn2m35gsddaIbFbRBXyPkmf7qhLwX54uFx6ZyVKOnq39RvJvBQw2u6yQaa4r01raYtpyZC3JA4uWAM55Anqt1LutvZbtUM07VRaZobnVte8eEhIY8R9eYBPQHHLouJrdqN+Ky2Wi3TWG3+BtAApCKyLLMHOPX1S57Vb/XO/0d7r7XQvraGJ0MEhrYRhhaW49ZIKJ1THaHbds98dd3un1sLq+1yz2O2+6ad1PCQ0vy8Hr1HvV0e29ondG+TC3BllnkfE+YMfS+9/JsMhzz64Ycetdm2k2W3FsNs1yy8Wqnhnu9q9z0obVscHyZeTkg+9/OXSLNsDvRaa1tZSWGiZO2OSIE18RGJGFjv/q4jKI6fh2Gs3o3dZo6PVNXb9Pi0VpY1snubPES4tAPry0rfak3T3qp7tbbPd7bp59TWOc6mbJBlh4Wkl2e4AAkrg5dnN95dLM0vJZaV1rj8H4OE10WGlj3OBB+F55/At7ddse0Fdbpb7jW2yiknoJHvpuKrhwA/PEzHmIJGPMien4byPdjeRlfNp6Kl0xLK2n90iKCnyyWIkglvTPNp7h0Wxp93t1rbabteaS16cjo7TUilqqiClH55PDkedue9aW7V9oCPV0eporLb4K5kDqePwVVAGMjP9AN6dST86+Vs2i36t+mJ9OU1moRbZ2uE0TqyFxkLncRcSeec8+qIcnNvLu/FeLZFxaXmrbjTeGpvBwni8Hkj3xI5DIPnXwpt7d3Bp6PUcdPp5lDV8BEop+cjy4NwT3uyenmyuMp9lN8YbnQXJlloPdFBTimhJrojiIFzgOfflzl8WbI73t03R6bNopfwbR1XuqCP3dFkSjPM+rn9iJnTltSb67taOmioa5mn43yM8MG09OSAHc+eQOfwZUiaz3O1szbzby8WVtpN3v0bZJnVMOY4z4Bz3EHu5A/Mot1js1vnqqeCou1ht7pYYhE0srogeEdO9STrXbjcV+2G3dFp210s16sMbRVwSzsDB+QdG4c/wA7m5TtXUOr0e6288+oK6ysbpZtRBT+6pnup3eBkjI/OBAJd0I5juWxsu7O7UmmLrebXTaVZQUT3sqWtpSwvxkEjlgjr1IWKXbff6l1FWX4WC1uqKun9zvYK2JrGxjuABHD1PRcRWbJb4S2iazC100FBNUmpkiiuTGtc45yHYPMc+h78Kq3TXWHNVm7G7Ntt9sq5KbSkcd64RTsipSHvB5Av97jHqyV93bo7zWfUsOnJaXS9BXVDZnFxp+GLhiB4nEgfm4Bwthc9q997hb7VRz6dtobasGle2uj4uXMcs4P0L61W2naCq9Q0N8q7HaqqqoYZKeIyVUJa6N7S1wcOh5FSh9bfvFvJWagrrJRUNglrLXE6eYx0vvWs4Q7LD3kggjplZs28+8d20hWasoY9OOoIM+6C6Ah78HBIGMY+Era2nbHtBWm/XG+UNqt0VbcXwmdxq4cObGCGtAPINAOOS0Wza3fqi0pWado7BbIaGtc50jPdkeRxHLgMnHzdyJiI9YfCPe/c2jvFBQU9DpuGrusTJKZzKT84Pc5o4vNzaVJOymvda1W8UmjtS09gjAonSvNvpy08iMNyQPP5lHFRtFvhNDaAdL2gTWct9zTNrIhIWtJIDueSMkqRdk9AbmUW8Z1jrSy0VHE+jfCXwVQflxII5ZJ7lBKyTG8JOTn/Ba1pZ068+/ktShAiIgpB2+fLDafk/D94qETt8+WG0/J+H7xUIrC76IiqCIiAiIpSIiJsYWURNoERE2nYiIm0CIibBER3JNgsIFlNpERE2hhFlE2CIibGFlETaRERDYiIoQIiIKQdvnyw2n5Pw/eKhE7fPlhtPyfh+8VCKwu+iIqgiIgIiICIiAiIgIiICIiAiIgIeaIgwsoiAiIgIiICIiAiIgIiICIiAiIgpB2+fLDafk/D94qETt8+WG0/J+H7xUIrC76IiqCIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiCkHb58sNp+T8P3ioRO3z5YbT8n4fvFQisLvoiKoIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiIKQdvnyw2n5Pw/eKhE7fPlhtPyfh+8VCKwu+i2VjulBe7PSXe2VDKmirIWzQSsOQ5rhkFb1VBERAREQEREBERAREQEREBERAREQEREBERAREQEREBERAREQEREBEWiWSOKJ8sr2xxsaXOc44DQOpJ7ggpD293NdvFawCCW2CEH1Hw9Qf8UUd9obWcWu92bxfKR/HQNeKaiP8AWhjHCHD1OPE7/eRWE+9gK93WptuobFUVssttojFLTQOwRE55dx8J6gHAOOmcnqSrUIigERFAIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAiIgIiICIiAoE7cN7utp2ngprbWy0sVxrRTVYjwDLEWOJYT1AJAzjqOR5FEUiiqIikf/2Q==";

// ── LOGIN SCREEN — with AMH logo and updated title ────────────────────────────
function LoginScreen({ onLogin }) {
  const [emailOrUser, setEmailOrUser] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verifEmail, setVerifEmail] = useState("");
  const [resendSent, setResendSent] = useState(false);
  const { isMobile } = useResponsive();

  // Auto-login from cookie on mount, handle OAuth errors in URL
  useEffect(() => {
    (async () => {
      // Handle OAuth error codes from Google OAuth callback (?error=)
      const searchParams = new URLSearchParams(window.location.search);
      const oauthErrorCode = searchParams.get("error") || searchParams.get("oauthError");
      if (oauthErrorCode) {
        const errorMessages = {
          google_failed:      "Google sign-in failed. Please try again.",
          not_alphamedia:     "Access restricted. Only @alphamedia.co.zw accounts are permitted.",
          account_not_found:  "Account not found. Contact your administrator to be added to the system.",
          account_inactive:   "Your account has been deactivated. Contact your administrator.",
          email_not_verified: "Please verify your email address before signing in.",
          google_error:       "A technical error occurred. Please try again or use email/password login.",
          notConfigured:      "Google Sign-In is not configured on this server.",
        };
        setError(errorMessages[oauthErrorCode] || decodeURIComponent(oauthErrorCode) || "Google authentication failed. Please try again.");
        searchParams.delete("error");
        searchParams.delete("oauthError");
        window.history.replaceState(null, "", window.location.pathname + (searchParams.toString() ? `?${searchParams}` : ""));
      }

      // Check URL fragment for Google OAuth redirect token (#token=)
      const hash = window.location.hash;
      if (hash.includes("token=")) {
        const params = new URLSearchParams(hash.slice(1));
        const token = params.get("token");
        if (token) {
          setAccessToken(token);
          window.history.replaceState(null, "", window.location.pathname);
          try {
            const res = await fetch(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" });
            if (res.ok) { const u = await res.json(); onLogin(u); return; }
          } catch {}
        }
      }

      // Try silent refresh from httpOnly refreshToken cookie
      try {
        const res = await fetch(`${API_URL}/api/auth/refresh`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" } });
        if (res.ok) {
          const { accessToken } = await res.json();
          setAccessToken(accessToken);
          const meRes = await fetch(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${accessToken}` }, credentials: "include" });
          if (meRes.ok) { const u = await meRes.json(); onLogin(u); return; }
        }
      } catch {}

      // Try authToken cookie (set by Google OAuth success — httpOnly, read via /api/auth/me)
      try {
        const meRes = await fetch(`${API_URL}/api/auth/me`, { credentials: "include" });
        if (meRes.ok) { const u = await meRes.json(); onLogin(u); }
      } catch {}
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = async () => {
    if (!emailOrUser || !password) { setError("Please enter your email/username and password."); return; }
    setLoading(true); setError(""); setNeedsVerification(false);
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: emailOrUser.trim(), password, rememberMe }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 429) {
          setError("Too many login attempts. Please try again in 15 minutes.");
        } else if (data.needsVerification) {
          setNeedsVerification(true);
          setVerifEmail(data.email || emailOrUser);
        } else {
          setError(data.error || "Invalid credentials. Please try again.");
        }
        setLoading(false);
        return;
      }
      setAccessToken(data.accessToken);
      onLogin(data.user);
    } catch { setError("Network error — please check your connection."); }
    setLoading(false);
  };

  const handleResendVerification = async () => {
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: verifEmail }),
      });
      setResendSent(true);
    } catch {}
    setLoading(false);
  };

  const handleForgot = async () => {
    if (!forgotEmail) { setError("Enter your email address."); return; }
    setLoading(true); setError("");
    try {
      await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
      setForgotSent(true);
    } catch { setError("Network error — please try again."); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#020617", backgroundImage: "radial-gradient(ellipse at 20% 50%, #1e3a5f33 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, #3b1f6333 0%, transparent 60%)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Georgia, serif", padding: "20px 16px" }}>
      <div style={{ width: "100%", maxWidth: 440 }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <div style={{ width: 88, height: 88, borderRadius: 20, overflow: "hidden", border: "2px solid #2563EB44", boxShadow: "0 0 32px #2563eb33", background: "#fff" }}>
              <img src={AMH_LOGO_SRC} alt="Alpha Media Holdings" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
          </div>
          <h1 style={{ margin: 0, color: "#f1f5f9", fontSize: isMobile ? 22 : 26, fontWeight: 900, letterSpacing: "-0.6px", fontFamily: "Georgia, serif" }}>
            Editorial Department Dashboard
          </h1>
          <p style={{ margin: "8px 0 0", color: "#64748b", fontSize: 13 }}>Alpha Media Holdings · Secure Portal</p>
          <div style={{ width: 80, height: 3, background: "linear-gradient(90deg, #2563EB, #7C3AED)", margin: "18px auto 0", borderRadius: 3 }} />
        </div>

        <div style={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 24, padding: isMobile ? 24 : 36, boxShadow: "0 32px 100px #00000077" }}>
          {forgotMode ? (
            // ── Forgot Password ──────────────────────────────────────────────
            forgotSent ? (
              <div>
                <div style={{ fontSize: 32, textAlign: "center", marginBottom: 12 }}>📬</div>
                <div style={{ textAlign: "center", fontSize: 14, color: "#f1f5f9", fontWeight: 700, marginBottom: 8 }}>Check your inbox</div>
                <div style={{ textAlign: "center", fontSize: 13, color: "#64748b", marginBottom: 24 }}>
                  If that email is registered, a reset link has been sent.
                </div>
                <button onClick={() => { setForgotMode(false); setForgotSent(false); setForgotEmail(""); }}
                  style={{ width: "100%", background: "none", border: "1px solid #334155", borderRadius: 12, padding: "13px", color: "#94a3b8", fontSize: 14, cursor: "pointer", fontFamily: "Georgia, serif" }}>
                  ← Back to Sign In
                </button>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 20 }}>Enter your email and we'll send a reset link.</div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: "block", fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 8, fontWeight: 700 }}>Email Address</label>
                  <input type="email" value={forgotEmail} onChange={e => { setForgotEmail(e.target.value); setError(""); }}
                    placeholder="you@alphamedia.co.zw" onKeyDown={e => e.key === "Enter" && handleForgot()}
                    style={{ width: "100%", background: "#1e293b", border: "1px solid #334155", borderRadius: 12, padding: "13px 16px", color: "#f1f5f9", fontSize: 14, outline: "none", boxSizing: "border-box", transition: "border-color 0.2s" }}
                    onFocus={e => e.target.style.borderColor = "#2563EB"} onBlur={e => e.target.style.borderColor = "#334155"} />
                </div>
                {error && (
                  <div style={{ background: "#DC2626", border: "1px solid #DC262680", borderRadius: 10, padding: "10px 14px", color: "#ffffff", fontSize: 13, marginBottom: 16, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                    <span>⚠️ {error}</span>
                    <button onClick={() => setError("")} style={{ background: "none", border: "none", color: "#ffffff", fontSize: 16, cursor: "pointer", padding: 0, lineHeight: 1, flexShrink: 0 }} aria-label="Dismiss">×</button>
                  </div>
                )}
                <button onClick={handleForgot} disabled={loading}
                  style={{ width: "100%", background: loading ? "#334155" : "linear-gradient(135deg, #2563EB, #7C3AED)", border: "none", borderRadius: 12, padding: "15px", color: "#fff", fontSize: 15, fontWeight: 800, cursor: loading ? "default" : "pointer", fontFamily: "Georgia, serif", marginBottom: 12 }}>
                  {loading ? "Sending…" : "Send Reset Link"}
                </button>
                <button onClick={() => { setForgotMode(false); setError(""); }}
                  style={{ width: "100%", background: "none", border: "none", color: "#64748b", fontSize: 13, cursor: "pointer", padding: "8px" }}>
                  ← Back to Sign In
                </button>
              </div>
            )
          ) : (
            // ── Sign In ──────────────────────────────────────────────────────
            <>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: "block", fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 8, fontWeight: 700 }}>Email or Username</label>
                <input type="text" value={emailOrUser} onChange={e => { setEmailOrUser(e.target.value); setError(""); }} placeholder="you@alphamedia.co.zw"
                  style={{ width: "100%", background: "#1e293b", border: "1px solid #334155", borderRadius: 12, padding: "13px 16px", color: "#f1f5f9", fontSize: 14, outline: "none", boxSizing: "border-box", transition: "border-color 0.2s" }}
                  onFocus={e => e.target.style.borderColor = "#2563EB"} onBlur={e => e.target.style.borderColor = "#334155"}
                  onKeyDown={e => e.key === "Enter" && handleLogin()} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 8, fontWeight: 700 }}>Password</label>
                <div style={{ position: "relative" }}>
                  <input type={showPassword ? "text" : "password"} value={password} onChange={e => { setPassword(e.target.value); setError(""); }} placeholder="Enter your password"
                    style={{ width: "100%", background: "#1e293b", border: "1px solid #334155", borderRadius: 12, padding: "13px 44px 13px 16px", color: "#f1f5f9", fontSize: 14, outline: "none", boxSizing: "border-box", transition: "border-color 0.2s" }}
                    onFocus={e => e.target.style.borderColor = "#2563EB"} onBlur={e => e.target.style.borderColor = "#334155"}
                    onKeyDown={e => e.key === "Enter" && handleLogin()} />
                  <button type="button" onClick={() => setShowPassword(s => !s)}
                    style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: 4, display: "flex", alignItems: "center", lineHeight: 1 }}
                    aria-label={showPassword ? "Hide password" : "Show password"}>
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, color: "#64748b" }}>
                  <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)}
                    style={{ width: 14, height: 14, cursor: "pointer", accentColor: "#2563EB" }} />
                  Remember me
                </label>
                <button onClick={() => { setForgotMode(true); setError(""); }}
                  style={{ background: "none", border: "none", color: "#2563EB", fontSize: 12, cursor: "pointer", padding: 0, textDecoration: "underline" }}>
                  Forgot password?
                </button>
              </div>
              {needsVerification && (
                <div style={{ background: "#f59e0b11", border: "1px solid #f59e0b44", borderRadius: 12, padding: "14px 16px", marginBottom: 16 }}>
                  <div style={{ color: "#f59e0b", fontSize: 13, fontWeight: 700, marginBottom: 6 }}>📧 Email Verification Required</div>
                  <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 10 }}>Please verify your email address before signing in. Check your inbox for a verification link.</div>
                  {resendSent ? (
                    <div style={{ color: "#22c55e", fontSize: 12 }}>✓ Verification email sent. Check your inbox.</div>
                  ) : (
                    <button onClick={handleResendVerification} disabled={loading}
                      style={{ background: "none", border: "1px solid #f59e0b44", borderRadius: 8, padding: "6px 14px", color: "#f59e0b", fontSize: 12, cursor: loading ? "default" : "pointer", fontFamily: "Georgia, serif" }}>
                      {loading ? "Sending…" : "Resend verification email"}
                    </button>
                  )}
                </div>
              )}
              {error && (
                <div style={{ background: "#DC2626", border: "1px solid #DC262680", borderRadius: 10, padding: "10px 14px", color: "#ffffff", fontSize: 13, marginBottom: 16, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                  <span>⚠️ {error}</span>
                  <button onClick={() => setError("")} style={{ background: "none", border: "none", color: "#ffffff", fontSize: 16, cursor: "pointer", padding: 0, lineHeight: 1, flexShrink: 0 }} aria-label="Dismiss">×</button>
                </div>
              )}
              <button onClick={handleLogin} disabled={loading}
                style={{ width: "100%", background: loading ? "#334155" : "linear-gradient(135deg, #2563EB, #7C3AED)", border: "none", borderRadius: 12, padding: "15px", color: "#fff", fontSize: 15, fontWeight: 800, cursor: loading ? "default" : "pointer", letterSpacing: 0.5, transition: "all 0.2s", boxShadow: loading ? "none" : "0 4px 20px #2563eb44", fontFamily: "Georgia, serif", marginBottom: 16 }}
                onMouseEnter={e => !loading && (e.currentTarget.style.transform = "scale(1.01)")}
                onMouseLeave={e => (e.currentTarget.style.transform = "scale(1)")}>
                {loading ? "Authenticating..." : "Sign In →"}
              </button>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <div style={{ flex: 1, height: 1, background: "#1e293b" }} />
                <span style={{ fontSize: 11, color: "#475569" }}>or</span>
                <div style={{ flex: 1, height: 1, background: "#1e293b" }} />
              </div>
              <GoogleAuthButton disabled={loading} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Period multipliers (Day / Week / Month relative scale) ────────────────────
// ── Unified Date-Range System ─────────────────────────────────────────────────
// Users pick a full date (Day + Month + Year) for FROM and TO.
// e.g. Day 1 Jan 2020 → Day 30 Jan 2025

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: CURRENT_YEAR - 2019 }, (_, i) => 2020 + i);
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

// Compute total days between two full dates
function dateToDays({ day, month, year }) {
  return year * 365 + (month - 1) * 30 + day;
}

function daysBetween(from, to) {
  const d = dateToDays(to) - dateToDays(from);
  return Math.max(1, d + 1);
}

function formatDate({ day, month, year }) {
  return `${MONTHS[month - 1]} ${day}, ${year}`;
}

function buildDateRangeLabel(from, to, rangeMode) {
  if (!rangeMode) return formatDate(from);
  return `${formatDate(from)}  →  ${formatDate(to)}`;
}

// ── Sparkline trend generator (deterministic, no Math.random) ────────────────
function makeSparkline(finalVal, changePct, pts = 7) {
  const v = typeof finalVal === "string" ? parseFloat(String(finalVal).replace(/[^0-9.]/g, "")) : Number(finalVal);
  if (!v || !isFinite(v) || v <= 0) return [];
  const start = changePct != null ? v / (1 + Math.max(-90, changePct) / 100) : v * 0.82;
  const noise = [0.07, -0.04, 0.11, -0.02, 0.08, -0.06, 0];
  return Array.from({ length: pts }, (_, i) => {
    const progress = i / (pts - 1);
    return Math.max(1, Math.round(start + (v - start) * progress + v * noise[i]));
  });
}

// ── Skeleton shimmer card ─────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars -- kept for a future loading state
function SkeletonCard({ height = 120, theme: t }) {
  return (
    <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 16, height, overflow: "hidden", position: "relative" }}>
      <div style={{ position: "absolute", inset: 0, background: `linear-gradient(90deg, transparent 0%, ${t.surface2} 50%, transparent 100%)`, animation: "shimmer 1.6s infinite", backgroundSize: "200% 100%" }} />
    </div>
  );
}

// ── CSV Export ────────────────────────────────────────────────────────────────
function exportCSV(rows, filename) {
  const header = Object.keys(rows[0]).join(",");
  const body = rows.map(r => Object.values(r).map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename + ".csv"; a.click();
  URL.revokeObjectURL(url);
}

// ── PDF Export ────────────────────────────────────────────────────────────────
// kpis: [{label, value}]  ·  platforms: [{label, totalUsers, newUsers, sessions, engagementRate, connected}]
function exportPDF(title, periodLabel, kpis, platforms) {
  const win = window.open("", "_blank");
  if (!win) return;
  const now = new Date().toLocaleString();
  const fmt = v => (v == null ? "No GA4 data" : typeof v === "number" ? v.toLocaleString() : v);
  win.document.write(`
    <html><head><title>${title}</title>
    <style>
      body{font-family:Georgia,serif;padding:40px;color:#0f172a;}
      h1{font-size:24px;margin-bottom:4px;}
      p{color:#64748b;font-size:13px;margin-bottom:24px;}
      .period-badge{display:inline-block;background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:4px 12px;color:#1d4ed8;font-weight:700;font-size:13px;margin-bottom:20px;}
      table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:24px;}
      th{background:#f1f5f9;padding:8px 12px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.8px;border-bottom:2px solid #e2e8f0;}
      td{padding:10px 12px;border-bottom:1px solid #e2e8f0;}
      .kpi-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin-bottom:28px;}
      .kpi{background:#f8fafc;border:1px solid #e2e8f0;border-radius:12px;padding:16px;}
      .kpi-val{font-size:22px;font-weight:900;color:#2563EB;}
      .kpi-lbl{font-size:10px;color:#64748b;text-transform:uppercase;letter-spacing:.8px;margin-top:4px;}
      .footer{margin-top:32px;font-size:11px;color:#94a3b8;border-top:1px solid #e2e8f0;padding-top:12px;}
    </style></head><body>
    <h1>Alpha Media Holdings — ${title}</h1>
    <p>Generated: ${now} &nbsp;|&nbsp; Editorial Department Dashboard</p>
    <div class="period-badge">📅 ${periodLabel}</div>
    <div class="kpi-grid">
      ${kpis.map(k => `<div class="kpi"><div class="kpi-val">${fmt(k.value)}</div><div class="kpi-lbl">${k.label}</div></div>`).join("")}
    </div>
    <table>
      <tr><th>Platform</th><th>Total Users</th><th>New Users</th><th>Sessions</th><th>Engagement</th></tr>
      ${platforms.map(p => `<tr><td>${p.label}${p.connected === false ? " (not connected)" : ""}</td><td>${fmt(p.totalUsers)}</td><td>${fmt(p.newUsers)}</td><td>${fmt(p.sessions)}</td><td>${p.engagementRate || "—"}</td></tr>`).join("")}
    </table>
    <div class="footer">Alpha Media Holdings · Editorial Department Dashboard · Confidential${platforms.length === 0 ? " · GA4 not connected — no live data available" : ""}</div>
    </body></html>`);
  win.document.close();
  setTimeout(() => { win.print(); }, 400);
}

// ── Styled Select ─────────────────────────────────────────────────────────────
function StyledSelect({ value, onChange, options, theme: t, width = 90, label }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {label && <span style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>{label}</span>}
      <div style={{ position: "relative", display: "inline-block" }}>
        <select
          value={value}
          onChange={e => onChange(Number(e.target.value))}
          style={{
            appearance: "none", WebkitAppearance: "none",
            background: t.surface2, border: `1px solid ${t.border2}`,
            borderRadius: 8, padding: "6px 28px 6px 10px",
            fontSize: 12, fontWeight: 700, color: t.text,
            cursor: "pointer", outline: "none", width,
            fontFamily: "Georgia, serif", transition: "border-color 0.2s",
          }}
          onFocus={e => e.target.style.borderColor = "#2563EB"}
          onBlur={e => e.target.style.borderColor = t.border2}
        >
          {options.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <span style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", fontSize: 9, color: t.textMuted }}>▼</span>
      </div>
    </div>
  );
}

// ── DatePicker — picks Day + Month + Year as one unit ─────────────────────────
function DatePicker({ value, onChange, theme: t, label }) {
  const dayOpts   = DAYS.map(d => ({ value: d, label: `Day ${d}` }));
  const monthOpts = MONTHS.map((m, i) => ({ value: i + 1, label: m }));
  const yearOpts  = YEARS.map(y => ({ value: y, label: String(y) }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {label && (
        <span style={{ fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, fontWeight: 800 }}>{label}</span>
      )}
      <div style={{ display: "flex", gap: 6, alignItems: "flex-end", flexWrap: "wrap" }}>
        <StyledSelect value={value.day}   onChange={v => onChange({ ...value, day: v })}   options={dayOpts}   theme={t} width={80}  label="Day" />
        <StyledSelect value={value.month} onChange={v => onChange({ ...value, month: v })} options={monthOpts} theme={t} width={72}  label="Month" />
        <StyledSelect value={value.year}  onChange={v => onChange({ ...value, year: v })}  options={yearOpts}  theme={t} width={78}  label="Year" />
      </div>
    </div>
  );
}

// ── Dynamic date helpers ──────────────────────────────────────────────────────
function getTodayDate() {
  const n = new Date();
  return { day: n.getDate(), month: n.getMonth() + 1, year: n.getFullYear() };
}

function buildQuickPresets() {
  const today = new Date();
  const td = getTodayDate();
  const ago = n => { const d = new Date(today); d.setDate(d.getDate() - n); return { day: d.getDate(), month: d.getMonth() + 1, year: d.getFullYear() }; };
  const yr = today.getFullYear();
  return [
    { label: "Today",      from: td,                             to: td,                                         range: false },
    { label: "This Week",  from: ago(6),                         to: td,                                         range: true  },
    { label: "This Month", from: ago(29),                        to: td,                                         range: true  },
    { label: "This Year",  from: { day: 1, month: 1, year: yr }, to: td,                                         range: true  },
    { label: "Last Year",  from: { day: 1, month: 1, year: yr - 1 }, to: { day: 31, month: 12, year: yr - 1 },  range: true  },
    { label: "2020–2025",  from: { day: 1, month: 1, year: 2020 }, to: { day: 31, month: 12, year: 2025 },      range: true  },
  ];
}

// ── DashboardToolbar ──────────────────────────────────────────────────────────
const PLATFORM_KEY_LABELS = [
  { key: "newsday", label: "NewsDay" }, { key: "zimind", label: "ZimInd" },
  { key: "standard", label: "Standard" }, { key: "southerneye", label: "SouthernEye" },
];

function DashboardToolbar({ dateFilter, setDateFilter, activeTab, theme: t, isMobile, gaData = {} }) {
  const [open, setOpen] = useState(false);

  const { rangeMode, from, to } = dateFilter;
  const periodLabel = buildDateRangeLabel(from, to, rangeMode);

  const update = patch => setDateFilter(prev => ({ ...prev, ...patch }));

  // Ensure to is never before from
  const setFrom = newFrom => {
    update({ from: newFrom });
  };
  const setTo = newTo => {
    update({ to: newTo });
  };

  const totalDays = rangeMode ? daysBetween(from, to) : 1;

  const handleCSV = () => {
    const overviewPerPub = gaData.overviewPerPub || {};
    const rows = PLATFORM_KEY_LABELS.map(({ key, label }) => {
      const d = overviewPerPub[key];
      return {
        Platform: label,
        Connected: d?.connected === false ? "No" : d ? "Yes" : "Unknown",
        "Total Users": d?.totalUsers ?? "",
        "New Users": d?.newUsers ?? "",
        Sessions: d?.sessions ?? "",
        Engagement: d?.engagementRate ?? "",
        Period: periodLabel,
      };
    });
    exportCSV(rows, `AMH_${activeTab}_${from.year}${String(from.month).padStart(2,"0")}${String(from.day).padStart(2,"0")}_to_${to.year}${String(to.month).padStart(2,"0")}${String(to.day).padStart(2,"0")}`);
  };

  const handlePDF = () => {
    const titles = { overview:"Dashboard Overview", platforms:"Platform Breakdown", social:"Social Media Analytics", audience:"Audience Intelligence", demographics:"Age Demographics", trending:"Trending Stories" };
    const overviewPerPub = gaData.overviewPerPub || {};
    const kpis = [
      { label: "Total Users", value: gaData.overview?.totalUsers ?? null },
      { label: "Total Views", value: gaData.overview?.pageViews ?? null },
      { label: "Avg Engagement", value: gaData.overview?.engagementRate ?? null },
    ];
    const platforms = PLATFORM_KEY_LABELS
      .filter(({ key }) => overviewPerPub[key])
      .map(({ key, label }) => {
        const d = overviewPerPub[key];
        return { label, totalUsers: d.totalUsers, newUsers: d.newUsers, sessions: d.sessions, engagementRate: d.engagementRate, connected: d.connected };
      });
    exportPDF(titles[activeTab] || activeTab, periodLabel, kpis, platforms);
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>

      {/* Active filter pill — shows current range */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, background: t.surface2, border: `1px solid ${t.border2}`, borderRadius: 10, padding: "5px 12px", maxWidth: isMobile ? 140 : 260, overflow: "hidden" }}>
        <svg width="11" height="11" viewBox="0 0 16 16" fill="#2563EB"><path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/></svg>
        <span style={{ fontSize: 11, fontWeight: 700, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{periodLabel}</span>
      </div>

      {/* Filter toggle button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{ display: "flex", alignItems: "center", gap: 5, background: open ? "#2563EB" : t.surface2, border: `1px solid ${open ? "#2563EB" : t.border}`, borderRadius: 10, padding: "6px 12px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: open ? "#fff" : t.textSub, transition: "all 0.18s", fontFamily: "Georgia, serif", whiteSpace: "nowrap" }}>
        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M1.5 2.5a.5.5 0 0 1 .5-.5h12a.5.5 0 0 1 0 1H2a.5.5 0 0 1-.5-.5zm2 3a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 0 1H4a.5.5 0 0 1-.5-.5zm2 3a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 0 1H6a.5.5 0 0 1-.5-.5z"/></svg>
        {!isMobile && "Date Filter"}
        {open ? " ▲" : " ▼"}
      </button>

      {/* Divider */}
      <div style={{ width: 1, height: 28, background: t.border }} />

      {/* CSV */}
      <button onClick={handleCSV}
        style={{ display: "flex", alignItems: "center", gap: 5, background: t.surface2, border: `1px solid ${t.border}`, borderRadius: 10, padding: "6px 13px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: t.textSub, transition: "all 0.18s", fontFamily: "Georgia, serif" }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = "#059669"; e.currentTarget.style.color = "#059669"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = t.border; e.currentTarget.style.color = t.textSub; }}>
        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M2 3a1 1 0 0 1 1-1h7.586a1 1 0 0 1 .707.293l2.414 2.414A1 1 0 0 1 14 5.414V13a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3zm9 8H5v1h6v-1zm0-2H5v1h6V9zm0-2H5v1h6V7z"/></svg>
        {!isMobile && "CSV"}
      </button>

      {/* PDF */}
      <button onClick={handlePDF}
        style={{ display: "flex", alignItems: "center", gap: 5, background: "#1e293b", border: "1px solid #334155", borderRadius: 10, padding: "6px 13px", cursor: "pointer", fontSize: 11, fontWeight: 800, color: "#fff", transition: "all 0.18s", fontFamily: "Georgia, serif" }}
        onMouseEnter={e => { e.currentTarget.style.background = "#2563EB"; e.currentTarget.style.borderColor = "#2563EB"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "#1e293b"; e.currentTarget.style.borderColor = "#334155"; }}>
        <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M4 0h5.293A1 1 0 0 1 10 .293L13.707 4a1 1 0 0 1 .293.707V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2zm5.5 1.5v2a1 1 0 0 0 1 1h2L9.5 1.5zM5 10v1h6v-1H5zm0 2v1h3v-1H5z"/></svg>
        PDF
      </button>

      {/* ── Date filter dropdown ── */}
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 10px)", right: 0, zIndex: 300,
          background: t.surface, border: `1px solid ${t.border2}`, borderRadius: 18,
          padding: "20px 22px", boxShadow: "0 16px 60px #00000040", width: 360,
          display: "flex", flexDirection: "column", gap: 16,
        }}>
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="#2563EB"><path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/></svg>
              <span style={{ fontSize: 13, fontWeight: 900, color: t.text, fontFamily: "Georgia, serif" }}>Date Filter</span>
            </div>
            <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: t.textMuted, lineHeight: 1, padding: "0 4px" }}>✕</button>
          </div>

          {/* Mode toggle: Single date / Date range */}
          <div style={{ display: "flex", gap: 6, background: t.surface2, borderRadius: 10, padding: 3 }}>
            {[{ key: false, label: "📅 Single Date" }, { key: true, label: "📅 Date Range" }].map(m => (
              <button key={String(m.key)} onClick={() => update({ rangeMode: m.key })}
                style={{ flex: 1, background: rangeMode === m.key ? "#2563EB" : "transparent", border: "none", borderRadius: 8, padding: "8px 0", cursor: "pointer", fontSize: 11, fontWeight: 800, color: rangeMode === m.key ? "#fff" : t.textMuted, transition: "all 0.18s", fontFamily: "Georgia, serif" }}>
                {m.label}
              </button>
            ))}
          </div>

          {/* FROM date always shown */}
          <div style={{ background: t.surface2, borderRadius: 12, padding: "14px 16px", border: `1px solid ${t.border}` }}>
            <DatePicker
              value={from}
              onChange={setFrom}
              theme={t}
              label={rangeMode ? "From Date" : "Select Date"}
            />
          </div>

          {/* TO date only in range mode */}
          {rangeMode && (
            <div style={{ background: t.surface2, borderRadius: 12, padding: "14px 16px", border: `1px solid #2563EB44` }}>
              <DatePicker
                value={to}
                onChange={setTo}
                theme={t}
                label="To Date"
              />
            </div>
          )}

          {/* Summary */}
          <div style={{ background: rangeMode ? "#1e3a5f22" : t.surface2, borderRadius: 12, padding: "12px 16px", border: `1px solid ${rangeMode ? "#2563EB44" : t.border}` }}>
            <div style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6, fontWeight: 700 }}>Selected Period</div>
            <div style={{ fontSize: 14, color: "#2563EB", fontWeight: 900, fontFamily: "Georgia, serif", marginBottom: rangeMode ? 6 : 0 }}>{periodLabel}</div>
            {rangeMode && (
              <div style={{ fontSize: 11, color: t.textMuted }}>
                Span: <strong style={{ color: t.text }}>{totalDays} day{totalDays !== 1 ? "s" : ""}</strong>
                {totalDays >= 7 && <span style={{ color: t.textFaint }}> ({Math.round(totalDays / 7)} week{Math.round(totalDays / 7) !== 1 ? "s" : ""})</span>}
                {totalDays >= 30 && <span style={{ color: t.textFaint }}> · ~{Math.round(totalDays / 30)} month{Math.round(totalDays / 30) !== 1 ? "s" : ""}</span>}
                {totalDays >= 365 && <span style={{ color: t.textFaint }}> · ~{(totalDays / 365).toFixed(1)} year{totalDays / 365 >= 2 ? "s" : ""}</span>}
              </div>
            )}
          </div>

          {/* Quick presets */}
          <div>
            <div style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, fontWeight: 700 }}>Quick Presets</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {buildQuickPresets().map(p => (
                <button key={p.label}
                  onClick={() => { update({ from: p.from, to: p.to, rangeMode: p.range }); }}
                  style={{ background: t.surface2, border: `1px solid ${t.border2}`, borderRadius: 8, padding: "5px 11px", cursor: "pointer", fontSize: 10, fontWeight: 700, color: t.textSub, transition: "all 0.15s", fontFamily: "Georgia, serif" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "#2563EB"; e.currentTarget.style.color = "#2563EB"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = t.border2; e.currentTarget.style.color = t.textSub; }}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Apply */}
          <button onClick={() => setOpen(false)}
            style={{ background: "linear-gradient(135deg,#2563EB,#7C3AED)", border: "none", borderRadius: 12, padding: "11px", color: "#fff", fontSize: 13, fontWeight: 900, cursor: "pointer", fontFamily: "Georgia, serif", letterSpacing: 0.3, boxShadow: "0 4px 16px #2563eb33" }}>
            ✓ Apply Filter
          </button>
        </div>
      )}
    </div>
  );
}

// ── AudienceTab ───────────────────────────────────────────────────────────────
function AudienceTab({ gaData, gaConnected, gaChannels, gaChannelTotals, gaLocations, isMobile, isTablet, theme: t }) {
  const [selectedPub, setSelectedPub] = useState("all");

  const pubOptions = [
    { key: "all", label: "All Platforms" },
    { key: "newsday", label: "NewsDay", color: "#2563EB" },
    { key: "zimind", label: "ZimInd", color: "#7C3AED" },
    { key: "standard", label: "Standard", color: "#0891B2" },
    { key: "southerneye", label: "SouthernEye", color: "#059669" },
  ];

  const isAllPub = selectedPub === "all";
  const perPubAudience = !isAllPub ? gaData.audiencePerPub?.[selectedPub] : null;
  const perPubChannels = !isAllPub ? gaData.channelsPerPub?.[selectedPub] : null;

  return (
    <div>
      {/* Publication selector */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        <span style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, marginRight: 4 }}>Publication:</span>
        {pubOptions.map(p => (
          <button key={p.key} onClick={() => setSelectedPub(p.key)}
            style={{ background: selectedPub === p.key ? (p.color || "#2563EB") : t.surface, border: `1px solid ${selectedPub === p.key ? (p.color || "#2563EB") : t.border}`, borderRadius: 8, padding: "5px 13px", cursor: "pointer", fontSize: 11, fontWeight: selectedPub === p.key ? 800 : 600, color: selectedPub === p.key ? "#fff" : t.textMuted, transition: "all 0.18s", fontFamily: "Georgia, serif" }}>
            {p.label}
          </button>
        ))}
      </div>

      {isAllPub ? (
        <>
          {/* ── All-platform acquisition (original content) ── */}
          <div style={{ marginBottom: 28 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
              <div style={{ width: 4, height: 22, background: "linear-gradient(180deg, #2563EB, #7C3AED)", borderRadius: 2 }} />
              <h3 style={{ margin: 0, fontSize: 13, color: t.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>User Acquisition</h3>
              {gaConnected && gaChannels.length > 0
                ? <span style={{ fontSize: 10, color: "#22c55e", background: "#22c55e11", padding: "3px 10px", borderRadius: 20, border: "1px solid #22c55e33", fontWeight: 700 }}>● GA4 Live Data</span>
                : gaConnected
                  ? <span style={{ fontSize: 10, color: "#f59e0b", background: "#f59e0b11", padding: "3px 10px", borderRadius: 20, border: "1px solid #f59e0b33" }}>No GA4 data available for this period</span>
                  : <span style={{ fontSize: 10, color: t.textFaint, background: t.surface2, padding: "3px 10px", borderRadius: 20, border: `1px solid ${t.border}` }}>GA4 Offline · Sample Data</span>
              }
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr" : "2fr 1fr", gap: 20, marginBottom: 20 }}>
              <AcquisitionChannelTable isMobile={isMobile} theme={t} channels={gaChannels} totals={gaChannelTotals} />
              <ChannelEngagementCard isMobile={isMobile} theme={t} gaOverview={gaData.overview} />
            </div>
            <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: isMobile ? 16 : 24 }}>
              <h3 style={{ margin: "0 0 18px", fontSize: 13, color: t.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Channel Share · All Users</h3>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 2fr", gap: 24, alignItems: "center" }}>
                <DonutChart data={Object.fromEntries(gaChannels.map(ch => [ch.channel, ch.totalUsers]))} colors={gaChannels.map(ch => ch.color)} theme={t} />
                <div>
                  {gaChannels.map(ch => (
                    <div key={ch.channel} style={{ display: "grid", gridTemplateColumns: "130px 1fr 60px 70px", gap: 8, alignItems: "center", marginBottom: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 8, height: 8, borderRadius: 2, background: ch.color }} /><span style={{ fontSize: 11, color: t.text }}>{ch.channel}</span></div>
                      <div style={{ background: t.surface2, borderRadius: 4, height: 7 }}><div style={{ width: `${ch.totalUsersPct}%`, height: "100%", background: ch.color, borderRadius: 4, transition: "width 1s ease" }} /></div>
                      <span style={{ fontSize: 11, color: ch.color, fontWeight: 800, textAlign: "right" }}>{ch.totalUsersPct}%</span>
                      <span style={{ fontSize: 10, color: t.textFaint, textAlign: "right" }}>{ch.totalUsers.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {gaData.audience && (
            <div style={{ marginTop: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <div style={{ width: 4, height: 22, background: "linear-gradient(180deg, #059669, #0891B2)", borderRadius: 2 }} />
                <h3 style={{ margin: 0, fontSize: 13, color: t.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Audience & Geography</h3>
                <span style={{ fontSize: 10, color: "#22c55e", background: "#22c55e11", padding: "3px 10px", borderRadius: 20, border: "1px solid #22c55e33", fontWeight: 700 }}>● GA4 Live</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 16, marginBottom: 16 }}>
                {[
                  { label: "Total Users", value: (gaData.audience.totalUsers || 0).toLocaleString(), color: "#2563EB", sub: "All visitors" },
                  { label: "New Users", value: (gaData.audience.newUsers || 0).toLocaleString(), color: "#059669", sub: `${gaData.audience.totalUsers ? Math.round((gaData.audience.newUsers / gaData.audience.totalUsers) * 100) : 0}% of total` },
                  { label: "Returning Users", value: (gaData.audience.returningUsers || 0).toLocaleString(), color: "#7C3AED", sub: `${gaData.audience.totalUsers ? Math.round((gaData.audience.returningUsers / gaData.audience.totalUsers) * 100) : 0}% of total` },
                ].map((k, i) => (
                  <div key={i} style={{ background: t.surface, border: `1px solid ${k.color}22`, borderRadius: 16, padding: "16px 18px", position: "relative", overflow: "hidden" }}>
                    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: k.color }} />
                    <div style={{ fontSize: 22, fontWeight: 900, color: k.color, fontFamily: "Georgia, serif", marginBottom: 2 }}>{k.value}</div>
                    <div style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8 }}>{k.label}</div>
                    <div style={{ fontSize: 10, color: t.textFaint, marginTop: 2 }}>{k.sub}</div>
                  </div>
                ))}
              </div>
              <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 16, padding: 18 }}>
                <div style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12, fontWeight: 700 }}>Top Countries</div>
                {(gaData.audience.countries || []).slice(0, 8).map(c => (
                  <div key={c.country} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <MiniBar label={c.country} value={c.users} max={(gaData.audience.countries[0] || {}).users || 1} color="#2563EB" theme={t} />
                    </div>
                    {c.change != null && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: c.change >= 0 ? "#22c55e" : "#DC2626", marginLeft: 8, minWidth: 44, textAlign: "right" }}>
                        {c.change >= 0 ? "+" : ""}{c.change}%
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {gaData.channelsByDevice?.channels?.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <div style={{ width: 4, height: 22, background: "linear-gradient(180deg, #2563EB, #059669)", borderRadius: 2 }} />
                <h3 style={{ margin: 0, fontSize: 13, color: t.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Traffic Sources by Device</h3>
                <span style={{ fontSize: 10, color: "#22c55e", background: "#22c55e11", padding: "3px 10px", borderRadius: 20, border: "1px solid #22c55e33", fontWeight: 700 }}>● GA4 Live</span>
              </div>
              <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: isMobile ? 14 : 22, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 540 }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${t.border}` }}>
                      {["Channel", "Desktop", "Mobile", "Tablet", "Total", "Share"].map(h => (
                        <th key={h} style={{ padding: "8px 12px", textAlign: h === "Channel" ? "left" : "right", color: t.textMuted, fontWeight: 700, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.8 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {gaData.channelsByDevice.channels.map(ch => (
                      <tr key={ch.channel} style={{ borderBottom: `1px solid ${t.border}`, transition: "background 0.15s" }}
                        onMouseEnter={e => e.currentTarget.style.background = t.surface2}
                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                        <td style={{ padding: "10px 12px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                            <div style={{ width: 8, height: 8, borderRadius: 2, background: ch.color }} />
                            <span style={{ color: t.text, fontWeight: 700 }}>{ch.channel}</span>
                          </div>
                        </td>
                        {["Desktop", "Mobile", "Tablet"].map(dev => (
                          <td key={dev} style={{ padding: "10px 12px", textAlign: "right" }}>
                            {ch.devices[dev] ? (
                              <div>
                                <div style={{ fontSize: 11, fontWeight: 800, color: ch.color }}>{ch.devices[dev].sessions.toLocaleString()}</div>
                                <div style={{ fontSize: 9, color: t.textFaint }}>{ch.devices[dev].bounceRate} · {ch.devices[dev].avgDur}</div>
                              </div>
                            ) : <span style={{ color: t.textFaint }}>—</span>}
                          </td>
                        ))}
                        <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 800, color: t.text }}>{ch.total.toLocaleString()}</td>
                        <td style={{ padding: "10px 12px", textAlign: "right" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
                            <div style={{ width: 50, background: t.surface2, borderRadius: 3, height: 6 }}>
                              <div style={{ width: `${ch.sessionsPct}%`, height: "100%", background: ch.color, borderRadius: 3 }} />
                            </div>
                            <span style={{ fontSize: 10, color: ch.color, fontWeight: 700 }}>{ch.sessionsPct}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {/* ── Per-publication audience view ── */}
          {perPubAudience ? (
            <div>
              {/* Summary KPIs */}
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3,1fr)", gap: 16, marginBottom: 20 }}>
                {[
                  { label: "Total Users", value: (perPubAudience.totalUsers || 0).toLocaleString(), color: pubOptions.find(p => p.key === selectedPub)?.color || "#2563EB" },
                  { label: "New Users", value: (perPubAudience.newUsers || 0).toLocaleString(), color: "#059669" },
                  { label: "Returning Users", value: (perPubAudience.returningUsers || 0).toLocaleString(), color: "#7C3AED" },
                ].map((k, i) => (
                  <div key={i} style={{ background: t.surface, border: `1px solid ${k.color}22`, borderRadius: 16, padding: "16px 18px", position: "relative", overflow: "hidden" }}>
                    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: k.color }} />
                    <div style={{ fontSize: 22, fontWeight: 900, color: k.color, fontFamily: "Georgia, serif", marginBottom: 2 }}>{k.value}</div>
                    <div style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8 }}>{k.label}</div>
                  </div>
                ))}
              </div>

              {/* Countries */}
              {perPubAudience.countries?.length > 0 && (
                <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 16, padding: 18, marginBottom: 20 }}>
                  <div style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 12, fontWeight: 700 }}>Top Countries</div>
                  {perPubAudience.countries.slice(0, 8).map(c => (
                    <MiniBar key={c.country} label={c.country} value={c.users} max={(perPubAudience.countries[0] || {}).users || 1} color={pubOptions.find(p => p.key === selectedPub)?.color || "#2563EB"} theme={t} />
                  ))}
                </div>
              )}

              {/* Channel acquisition */}
              {perPubChannels && (
                <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: isMobile ? 16 : 22 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: t.text, marginBottom: 16 }}>Acquisition Channels</div>
                  {Object.entries(perPubChannels.channels || {}).map(([name, d]) => (
                    <div key={name} style={{ marginBottom: 10 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{ width: 8, height: 8, borderRadius: 2, background: d.color }} />
                          <span style={{ fontSize: 12, color: t.textSub }}>{name}</span>
                        </div>
                        <div style={{ display: "flex", gap: 12 }}>
                          <span style={{ fontSize: 11, color: t.textFaint }}>{d.sessions.toLocaleString()} sessions</span>
                          <span style={{ fontSize: 12, fontWeight: 800, color: d.color }}>{d.pct}%</span>
                        </div>
                      </div>
                      <div style={{ background: t.surface2, borderRadius: 3, height: 5 }}>
                        <div style={{ width: `${d.pct}%`, height: "100%", background: d.color, borderRadius: 3, transition: "width 1s ease" }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Channel × Device */}
              {perPubAudience.channelsByDevice?.length > 0 && (
                <div style={{ marginTop: 20, background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: isMobile ? 14 : 22, overflowX: "auto" }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: t.text, marginBottom: 14 }}>Traffic Sources by Device</div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 480 }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${t.border}` }}>
                        {["Channel", "Desktop", "Mobile", "Tablet"].map(h => (
                          <th key={h} style={{ padding: "8px 12px", textAlign: h === "Channel" ? "left" : "right", color: t.textMuted, fontWeight: 700, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.8 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {perPubAudience.channelsByDevice.map(ch => (
                        <tr key={ch.channel} style={{ borderBottom: `1px solid ${t.border}`, transition: "background 0.15s" }}
                          onMouseEnter={e => e.currentTarget.style.background = t.surface2}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <td style={{ padding: "9px 12px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                              <div style={{ width: 8, height: 8, borderRadius: 2, background: ch.color }} />
                              <span style={{ color: t.text, fontWeight: 700 }}>{ch.channel}</span>
                            </div>
                          </td>
                          {["Desktop", "Mobile", "Tablet"].map(dev => (
                            <td key={dev} style={{ padding: "9px 12px", textAlign: "right" }}>
                              {ch.devices[dev] ? (
                                <div>
                                  <div style={{ fontSize: 11, fontWeight: 800, color: ch.color }}>{ch.devices[dev].sessions.toLocaleString()}</div>
                                  <div style={{ fontSize: 9, color: t.textFaint }}>{ch.devices[dev].bounceRate}</div>
                                </div>
                              ) : <span style={{ color: t.textFaint }}>—</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: 40, textAlign: "center" }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>👥</div>
              <div style={{ fontSize: 14, color: t.textMuted, fontStyle: "italic" }}>
                {gaConnected ? "No per-publication audience data. Configure per-publication GA4 property IDs in the server environment." : "Connect GA4 to see per-publication audience data."}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── TechnologyTab ─────────────────────────────────────────────────────────────
function TechnologyTab({ gaData, gaConnected, isMobile, isTablet, theme: t }) {
  const [selectedPub, setSelectedPub] = useState("all");
  const DEVICE_COLORS = ["#2563EB", "#7C3AED", "#0891B2"];
  const BROWSER_COLORS = ["#2563EB", "#0891B2", "#7C3AED", "#059669", "#DC2626", "#D97706", "#94a3b8", "#64748b"];
  const OS_COLORS = ["#059669", "#2563EB", "#7C3AED", "#0891B2", "#DC2626", "#D97706", "#94a3b8", "#64748b"];

  const pubOptions = [
    { key: "all", label: "All Platforms" },
    { key: "newsday", label: "NewsDay" },
    { key: "zimind", label: "ZimInd" },
    { key: "standard", label: "Standard" },
    { key: "southerneye", label: "SouthernEye" },
  ];

  const getPubData = () => {
    if (selectedPub === "all") {
      return gaData.demographics
        ? { devices: gaData.demographics.devices, browsers: gaData.demographics.browsers, os: gaData.demographics.os }
        : null;
    }
    return gaData.technologyPerPub?.[selectedPub] || null;
  };

  const pubData = getPubData();
  const hasData = !!(pubData && (Object.keys(pubData.devices || {}).length || (pubData.browsers || []).length));

  const statusBadge = gaConnected
    ? hasData
      ? <span style={{ fontSize: 10, color: "#22c55e", background: "#22c55e11", padding: "3px 10px", borderRadius: 20, border: "1px solid #22c55e33", fontWeight: 700 }}>● GA4 Live</span>
      : <span style={{ fontSize: 10, color: "#f59e0b", background: "#f59e0b11", padding: "3px 10px", borderRadius: 20, border: "1px solid #f59e0b33" }}>No data available</span>
    : <span style={{ fontSize: 10, color: t.textFaint, background: t.surface2, padding: "3px 10px", borderRadius: 20, border: `1px solid ${t.border}` }}>GA4 Offline</span>;

  return (
    <div>
      {/* Publication selector */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        <span style={{ fontSize: 10, color: t.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.8, marginRight: 4 }}>Publication:</span>
        {pubOptions.map(p => (
          <button key={p.key} onClick={() => setSelectedPub(p.key)}
            style={{ background: selectedPub === p.key ? "#2563EB" : t.surface, border: `1px solid ${selectedPub === p.key ? "#2563EB" : t.border}`, borderRadius: 8, padding: "5px 13px", cursor: "pointer", fontSize: 11, fontWeight: selectedPub === p.key ? 800 : 600, color: selectedPub === p.key ? "#fff" : t.textMuted, transition: "all 0.18s", fontFamily: "Georgia, serif" }}>
            {p.label}
          </button>
        ))}
        <div style={{ marginLeft: "auto" }}>{statusBadge}</div>
      </div>

      {!hasData ? (
        <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: 40, textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>💻</div>
          <div style={{ fontSize: 14, color: t.textMuted, fontStyle: "italic" }}>
            {gaConnected ? "No technology data available for this publication." : "Connect GA4 to see device, browser, and OS data."}
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3,1fr)", gap: 20 }}>
          {/* Device Breakdown */}
          <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 18, padding: 22 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <div style={{ width: 4, height: 18, background: "linear-gradient(180deg,#2563EB,#7C3AED)", borderRadius: 2 }} />
              <div style={{ fontSize: 12, fontWeight: 800, color: t.text, textTransform: "uppercase", letterSpacing: 0.8 }}>Device Breakdown</div>
            </div>
            <DonutChart
              data={Object.fromEntries(Object.entries(pubData.devices || {}).map(([k, v]) => [k, v.users || v]))}
              colors={DEVICE_COLORS} theme={t} />
            <div style={{ marginTop: 14 }}>
              {Object.entries(pubData.devices || {}).map(([name, val], i) => {
                const users = val.users ?? val;
                const pct = val.pct ?? 0;
                return (
                  <div key={name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: DEVICE_COLORS[i % DEVICE_COLORS.length], flexShrink: 0 }} />
                      <span style={{ fontSize: 12, color: t.textSub }}>{name}</span>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <span style={{ fontSize: 12, fontWeight: 800, color: DEVICE_COLORS[i % DEVICE_COLORS.length] }}>{pct}%</span>
                      <span style={{ fontSize: 10, color: t.textFaint, marginLeft: 6 }}>{users.toLocaleString()}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Browsers */}
          <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 18, padding: 22 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <div style={{ width: 4, height: 18, background: "linear-gradient(180deg,#0891B2,#059669)", borderRadius: 2 }} />
              <div style={{ fontSize: 12, fontWeight: 800, color: t.text, textTransform: "uppercase", letterSpacing: 0.8 }}>Browsers</div>
            </div>
            {(pubData.browsers || []).length === 0 ? (
              <div style={{ fontSize: 12, color: t.textFaint, fontStyle: "italic" }}>No browser data.</div>
            ) : (pubData.browsers || []).slice(0, 8).map((b, i) => {
              const max = (pubData.browsers[0] || {}).users || 1;
              return (
                <div key={b.name} style={{ marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                    <span style={{ fontSize: 12, color: t.textSub }}>{b.name}</span>
                    <span style={{ fontSize: 12, fontWeight: 800, color: BROWSER_COLORS[i % BROWSER_COLORS.length] }}>{b.users.toLocaleString()}</span>
                  </div>
                  <div style={{ background: t.surface2, borderRadius: 3, height: 5 }}>
                    <div style={{ width: `${Math.round((b.users / max) * 100)}%`, height: "100%", background: BROWSER_COLORS[i % BROWSER_COLORS.length], borderRadius: 3, transition: "width 1s ease" }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Operating Systems */}
          <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 18, padding: 22 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <div style={{ width: 4, height: 18, background: "linear-gradient(180deg,#DC2626,#D97706)", borderRadius: 2 }} />
              <div style={{ fontSize: 12, fontWeight: 800, color: t.text, textTransform: "uppercase", letterSpacing: 0.8 }}>Operating Systems</div>
            </div>
            {(pubData.os || []).length === 0 ? (
              <div style={{ fontSize: 12, color: t.textFaint, fontStyle: "italic" }}>No OS data.</div>
            ) : (pubData.os || []).slice(0, 8).map((o, i) => {
              const max = (pubData.os[0] || {}).users || 1;
              return (
                <div key={o.name} style={{ marginBottom: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                    <span style={{ fontSize: 12, color: t.textSub }}>{o.name}</span>
                    <span style={{ fontSize: 12, fontWeight: 800, color: OS_COLORS[i % OS_COLORS.length] }}>{o.users.toLocaleString()}</span>
                  </div>
                  <div style={{ background: t.surface2, borderRadius: 3, height: 5 }}>
                    <div style={{ width: `${Math.round((o.users / max) * 100)}%`, height: "100%", background: OS_COLORS[i % OS_COLORS.length], borderRadius: 3, transition: "width 1s ease" }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Per-publication overview if "all" selected and per-pub data available */}
      {selectedPub === "all" && gaData.technologyPerPub && (
        <div style={{ marginTop: 28 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <div style={{ width: 4, height: 22, background: "linear-gradient(180deg,#2563EB,#059669)", borderRadius: 2 }} />
            <h3 style={{ margin: 0, fontSize: 13, color: t.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>By Publication</h3>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr 1fr" : "repeat(4,1fr)", gap: 16 }}>
            {[
              { key: "newsday", label: "NewsDay", color: "#2563EB" },
              { key: "zimind", label: "ZimInd", color: "#7C3AED" },
              { key: "standard", label: "Standard", color: "#0891B2" },
              { key: "southerneye", label: "SouthernEye", color: "#059669" },
            ].map(pub => {
              const d = gaData.technologyPerPub[pub.key];
              if (!d) return null;
              const topDevice = Object.entries(d.devices || {}).sort((a, b) => (b[1].users || 0) - (a[1].users || 0))[0];
              const topBrowser = (d.browsers || [])[0];
              const topOS = (d.os || [])[0];
              return (
                <div key={pub.key} style={{ background: t.surface, border: `1px solid ${pub.color}33`, borderRadius: 16, padding: 16, cursor: "pointer", transition: "all 0.2s" }}
                  onClick={() => setSelectedPub(pub.key)}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 4px 16px ${pub.color}22`; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "translateY(0)"; }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 12 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: pub.color }} />
                    <span style={{ fontSize: 12, fontWeight: 800, color: t.text }}>{pub.label}</span>
                  </div>
                  {topDevice && <div style={{ fontSize: 11, color: t.textSub, marginBottom: 4 }}>📱 Top device: <strong style={{ color: pub.color }}>{topDevice[0]}</strong> ({topDevice[1].pct || 0}%)</div>}
                  {topBrowser && <div style={{ fontSize: 11, color: t.textSub, marginBottom: 4 }}>🌐 Top browser: <strong style={{ color: pub.color }}>{topBrowser.name}</strong></div>}
                  {topOS && <div style={{ fontSize: 11, color: t.textSub }}>💻 Top OS: <strong style={{ color: pub.color }}>{topOS.name}</strong></div>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── User Avatar Dropdown ──────────────────────────────────────────────────────
function UserMenu({ user, onLogout, onChangePw, theme: t }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const initials = (user.name || "?").split(/\s+/).map(w => w[0] || "").join("").slice(0, 2).toUpperCase();
  const isDark = t.name === "dark";

  useEffect(() => {
    if (!open) return;
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setOpen(o => !o)} title={user.name}
        style={{ width: 34, height: 34, borderRadius: "50%", background: "linear-gradient(135deg, #2563EB, #7C3AED)", border: `2px solid ${open ? "#7C3AED66" : "#2563EB33"}`, cursor: "pointer", fontSize: 12, fontWeight: 900, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s", flexShrink: 0 }}>
        {initials}
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, zIndex: 500, background: t.surface, border: `1px solid ${t.border2 || t.border}`, borderRadius: 14, padding: 6, minWidth: 200, boxShadow: isDark ? "0 16px 48px #00000066" : "0 8px 32px #00000022" }}>
          <div style={{ padding: "10px 14px 12px", borderBottom: `1px solid ${t.border}`, marginBottom: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: t.text }}>{user.name}</div>
            <div style={{ fontSize: 10, color: t.textMuted, textTransform: "capitalize" }}>{(user.role || "").replace(/_/g, " ")}</div>
          </div>
          {[
            { label: "Change password", icon: "🔑", action: () => { onChangePw(); setOpen(false); } },
            { label: "Sign out",        icon: "↩",  action: () => { onLogout(); setOpen(false); }, danger: true },
          ].map(item => (
            <button key={item.label} onClick={item.action}
              style={{ width: "100%", background: "none", border: "none", padding: "9px 14px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 9, fontSize: 12, color: item.danger ? "#DC2626" : t.textSub, textAlign: "left", fontFamily: "Georgia, serif", transition: "background 0.12s" }}
              onMouseEnter={e => e.currentTarget.style.background = t.surface2}
              onMouseLeave={e => e.currentTarget.style.background = "none"}>
              <span style={{ fontSize: 14 }}>{item.icon}</span>{item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Sidebar Navigation ────────────────────────────────────────────────────────
function Sidebar({ visibleTabs, activeTab, setActiveTab, theme: t, gaConnected, isMobile, isTablet }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const isDark = t.name === "dark";
  const compact = isTablet && !isMobile;
  const W = compact ? 64 : 224;
  const primary   = ["overview", "social", "audience", "goals", "technology"];
  const secondary = ["platforms", "demographics", "trending", "reports", "admin"];

  const navItem = (tab) => {
    const active = activeTab === tab.key;
    return (
      <button key={tab.key} onClick={() => { setActiveTab(tab.key); setMobileOpen(false); }}
        title={compact ? tab.label : undefined}
        style={{ width: compact ? 44 : "calc(100% - 12px)", margin: compact ? "3px auto" : "1px 6px", display: "flex", alignItems: "center", justifyContent: compact ? "center" : "flex-start", gap: 9, background: active ? (isDark ? "#1e3a5f" : "#eff6ff") : "transparent", border: "none", borderRadius: 9, padding: compact ? "10px 0" : "8px 12px", cursor: "pointer", fontSize: 12, fontWeight: active ? 800 : 500, color: active ? "#2563EB" : t.textMuted, fontFamily: "Georgia, serif", transition: "all 0.15s", borderLeft: !compact && active ? "3px solid #2563EB" : !compact ? "3px solid transparent" : "none" }}
        onMouseEnter={e => { if (!active) e.currentTarget.style.background = t.surface2; }}
        onMouseLeave={e => { if (!active) e.currentTarget.style.background = "transparent"; }}>
        <span style={{ fontSize: compact ? 17 : 14, lineHeight: 1, flexShrink: 0 }}>{tab.icon}</span>
        {!compact && <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tab.label}</span>}
      </button>
    );
  };

  const sidebarContent = (width) => (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Logo */}
      <div style={{ padding: compact ? "14px 0" : "14px 16px", display: "flex", alignItems: "center", gap: 10, borderBottom: `1px solid ${t.border}`, justifyContent: compact ? "center" : "flex-start", flexShrink: 0 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, overflow: "hidden", border: "1.5px solid #2563EB44", background: "#fff", flexShrink: 0 }}>
          <img src={AMH_LOGO_SRC} alt="AMH" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
        {!compact && (
          <div>
            <div style={{ fontSize: 13, fontWeight: 900, color: t.text, letterSpacing: "-0.3px" }}>Alpha Media</div>
            <div style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8 }}>Analytics</div>
          </div>
        )}
      </div>
      {/* Nav items */}
      <div className="sidebar-nav" style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
        {!compact && <div style={{ fontSize: 9, color: t.textFaint, textTransform: "uppercase", letterSpacing: 1, padding: "4px 18px 4px", fontWeight: 700 }}>Main</div>}
        {visibleTabs.filter(tab => primary.includes(tab.key)).map(navItem)}
        <div style={{ height: 1, background: t.border, margin: "6px 10px" }} />
        {!compact && <div style={{ fontSize: 9, color: t.textFaint, textTransform: "uppercase", letterSpacing: 1, padding: "4px 18px 4px", fontWeight: 700 }}>Tools</div>}
        {visibleTabs.filter(tab => secondary.includes(tab.key)).map(navItem)}
      </div>
      {/* GA status */}
      {!compact && (
        <div style={{ padding: "10px 14px", borderTop: `1px solid ${t.border}`, flexShrink: 0 }}>
          <span style={{ fontSize: 9, fontWeight: 700, padding: "3px 9px", borderRadius: 20, display: "inline-flex", alignItems: "center", gap: 5, background: gaConnected ? "#22c55e18" : t.surface2, color: gaConnected ? "#22c55e" : t.textMuted, border: `1px solid ${gaConnected ? "#22c55e44" : t.border}` }}>
            <span style={{ width: 5, height: 5, borderRadius: "50%", background: gaConnected ? "#22c55e" : t.textMuted, display: "inline-block" }} />
            {gaConnected ? "GA Connected" : "GA Offline"}
          </span>
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <>
        {mobileOpen && <div onClick={() => setMobileOpen(false)} style={{ position: "fixed", inset: 0, background: "#00000066", zIndex: 190 }} />}
        <div style={{ position: "fixed", top: 0, left: mobileOpen ? 0 : -260, height: "100vh", width: 240, background: t.topbar, borderRight: `1px solid ${t.border}`, zIndex: 200, transition: "left 0.25s cubic-bezier(.4,0,.2,1)", boxShadow: mobileOpen ? "4px 0 32px #00000044" : "none" }}>
          {sidebarContent(240)}
        </div>
        <button onClick={() => setMobileOpen(o => !o)} aria-label="Menu"
          style={{ position: "fixed", top: 13, left: 13, zIndex: 210, background: t.surface, border: `1px solid ${t.border}`, borderRadius: 9, padding: "6px 10px", fontSize: 17, color: t.textSub, cursor: "pointer", boxShadow: isDark ? "0 2px 10px #00000033" : "0 2px 8px #00000011" }}>
          {mobileOpen ? "✕" : "☰"}
        </button>
      </>
    );
  }

  return (
    <div style={{ width: W, flexShrink: 0, position: "fixed", top: 0, left: 0, height: "100vh", background: t.topbar, borderRight: `1px solid ${t.border}`, zIndex: 100 }}>
      {sidebarContent(W)}
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
function Dashboard({ user, onLogout, theme: t, onThemeToggle, gaData = {}, gaConnected = false, gaPreset = "7d", setGaPreset }) {
  const [activeTab, setActiveTab] = useState("overview");
  const { data: socialData, realtime: socialRealtime, loading: socialLoading, error: socialError } = useSocial();
  const { byPub: twitterByPub } = useTwitter();
  const [showChangePw, setShowChangePw] = useState(user?.forcePasswordChange || false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwError, setPwError] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [showShare, setShowShare] = useState(false);

  const handleChangePassword = async () => {
    if (pwForm.next !== pwForm.confirm) { setPwError("Passwords do not match"); return; }
    if (pwForm.next.length < 8) { setPwError("Password must be at least 8 characters"); return; }
    if (!/[A-Z]/.test(pwForm.next)) { setPwError("Must contain at least 1 uppercase letter"); return; }
    if (!/[0-9]/.test(pwForm.next)) { setPwError("Must contain at least 1 number"); return; }
    setPwLoading(true); setPwError("");
    try {
      const res = await fetchWithAuth(`${API_URL}/api/auth/change-password`, {
        method: "POST",
        body: JSON.stringify({ currentPassword: pwForm.current, newPassword: pwForm.next }),
      });
      const d = await res.json();
      if (!res.ok) { setPwError(d.error || "Failed to change password"); setPwLoading(false); return; }
      setShowChangePw(false);
      setPwForm({ current: "", next: "", confirm: "" });
    } catch { setPwError("Network error"); }
    setPwLoading(false);
  };
  const [dateFilter, setDateFilter] = useState(() => {
    const today = getTodayDate();
    return { rangeMode: false, from: today, to: today };
  });
  const { isMobile, isTablet } = useResponsive();
  const isDark = t.name === "dark";

  const gaChannels = gaData.channels || [];
  const gaChannelTotals = gaData.channelTotals || {};
  const gaLocations = gaData.locations || {};

  // Map tab keys to permission object keys
  const TAB_PERM = {
    overview: "overview", platforms: "platforms", social: "social_media",
    audience: "audience", demographics: "demographics",
    technology: "technology_devices", trending: "trending",
    goals: "goals",
  };

  const tabs = [
    { key: "overview",     label: "Overview",              icon: "📊" },
    { key: "platforms",    label: "Platforms",             icon: "📰" },
    { key: "social",       label: "Social Media",          icon: "📱" },
    { key: "audience",     label: "Audience",              icon: "👥" },
    { key: "demographics", label: "Demographics",          icon: "👤" },
    { key: "technology",   label: "Technology & Devices",  icon: "💻" },
    { key: "trending",     label: "Trending",              icon: "🔥" },
    { key: "goals",        label: "Goals",                 icon: "🎯" },
    { key: "reports",      label: "Reports",               icon: "📋" },
    { key: "admin",        label: "Admin",                 icon: "⚙️",  adminOnly: true },
  ];

  function canSeeTab(tab) {
    if (tab.key === "admin") {
      return user.role === "super_admin" || user.role === "admin";
    }
    if (user.role === "super_admin" || user.role === "admin" || user.role === "editor") return true;
    // viewer: check permissions object
    if (user.permissions) {
      const permKey = TAB_PERM[tab.key];
      return permKey ? !!user.permissions[permKey] : false;
    }
    // legacy access field fallback
    if (user.access === "all") return true;
    if (user.access === "executive") return ["overview", "platforms", "social", "audience", "demographics", "technology", "trending"].includes(tab.key);
    if (user.access === "technical") return ["overview", "platforms", "social", "technology"].includes(tab.key);
    if (user.access === "editorial") return ["overview", "platforms", "audience", "demographics", "technology", "trending"].includes(tab.key);
    return false;
  }

  const visibleTabs    = tabs.filter(canSeeTab);
  const permittedKeys  = new Set(visibleTabs.map(t => t.key));

  const pad = isMobile ? "16px" : isTablet ? "20px" : "28px";

  return (
    <div style={{ minHeight: "100vh", background: t.bg, fontFamily: "Georgia, serif", color: t.text, transition: "background 0.4s ease, color 0.4s ease" }}>
      {/* Force password change overlay */}
      {showChangePw && (
        <div style={{ position: "fixed", inset: 0, background: "#000000cc", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 20, padding: 32, width: "100%", maxWidth: 420, boxShadow: "0 32px 80px #00000088" }}>
            <h2 style={{ margin: "0 0 8px", color: "#f1f5f9", fontSize: 20, fontWeight: 900, fontFamily: "Georgia, serif" }}>{user?.forcePasswordChange ? "Set Your Password" : "Change Password"}</h2>
            <p style={{ margin: "0 0 24px", fontSize: 13, color: "#64748b" }}>{user?.forcePasswordChange ? "You must set a new password before continuing." : "Enter your current and new password."}</p>
            {[
              { label: "Current Password", key: "current", show: true },
              { label: "New Password",     key: "next",    show: true },
              { label: "Confirm Password", key: "confirm", show: true },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 6, fontWeight: 700 }}>{f.label}</label>
                <input type="password" value={pwForm[f.key]} onChange={e => setPwForm(p => ({ ...p, [f.key]: e.target.value }))}
                  style={{ width: "100%", background: "#1e293b", border: "1px solid #334155", borderRadius: 10, padding: "12px 14px", color: "#f1f5f9", fontSize: 14, outline: "none", boxSizing: "border-box" }}
                  onKeyDown={e => e.key === "Enter" && handleChangePassword()} />
              </div>
            ))}
            {pwError && <div style={{ background: "#DC262618", border: "1px solid #DC262640", borderRadius: 8, padding: "9px 12px", color: "#fca5a5", fontSize: 13, marginBottom: 14 }}>⚠️ {pwError}</div>}
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={handleChangePassword} disabled={pwLoading}
                style={{ flex: 1, background: "#2563EB", border: "none", borderRadius: 10, padding: "13px", color: "#fff", fontSize: 14, fontWeight: 800, cursor: pwLoading ? "default" : "pointer", fontFamily: "Georgia, serif" }}>
                {pwLoading ? "Saving…" : "Set Password"}
              </button>
              {!user?.forcePasswordChange && (
                <button onClick={() => { setShowChangePw(false); setPwError(""); }}
                  style={{ background: "none", border: "1px solid #334155", borderRadius: 10, padding: "13px 18px", color: "#94a3b8", fontSize: 14, cursor: "pointer" }}>
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Sidebar */}
      <Sidebar visibleTabs={visibleTabs} activeTab={activeTab} setActiveTab={setActiveTab} theme={t} gaConnected={gaConnected} isMobile={isMobile} isTablet={isTablet} />

      {/* Main content — offset for fixed sidebar */}
      <div style={{ marginLeft: isMobile ? 0 : isTablet ? 64 : 224, transition: "margin-left 0.2s ease", minHeight: "100vh" }}>
        {/* Slim topbar — no nav rows */}
        <div style={{ background: t.topbar, borderBottom: `1px solid ${t.border}`, padding: isMobile ? `8px 16px 8px 54px` : `8px ${pad}`, display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: 56, position: "sticky", top: 0, zIndex: 99, backdropFilter: "blur(12px)", boxShadow: isDark ? "0 1px 20px #00000033" : "0 1px 12px #00000011", transition: "background 0.4s ease" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            {isMobile ? (
              <>
                <div style={{ width: 28, height: 28, borderRadius: 7, overflow: "hidden", border: "1.5px solid #2563EB44", background: "#fff", flexShrink: 0 }}>
                  <img src={AMH_LOGO_SRC} alt="AMH" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                </div>
                <div style={{ fontSize: 13, fontWeight: 900, color: t.text, letterSpacing: "-0.3px" }}>AMH Analytics</div>
              </>
            ) : (
              <div>
                <div style={{ fontSize: 14, fontWeight: 900, color: t.text, letterSpacing: "-0.4px" }}>Alpha Media Analytics</div>
                <div style={{ fontSize: 10, color: t.textMuted, letterSpacing: 0.5 }}>Editorial Analytics Dashboard</div>
              </div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <AlertBell theme={t} />
            <button onClick={() => setShowShare(true)} title="Share view"
              style={{ background: t.surface2, border: `1px solid ${t.border}`, borderRadius: 8, padding: "7px 10px", cursor: "pointer", color: t.textSub, fontSize: 14 }}>
              🔗
            </button>
            <ThemeToggle isDark={isDark} onToggle={onThemeToggle} />
            <UserMenu user={user} onLogout={onLogout} onChangePw={() => setShowChangePw(true)} theme={t} />
          </div>
        </div>

        <div style={{ padding: pad, maxWidth: 1440, margin: "0 auto" }}>
        {/* ── Page header with period selector + export ── */}
        <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 14 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: isMobile ? 20 : 26, fontWeight: 900, color: t.text, letterSpacing: "-0.6px", fontFamily: "Georgia, serif" }}>
              {activeTab === "overview"     && "Dashboard Overview"}
              {activeTab === "platforms"    && "Platform Breakdown"}
              {activeTab === "social"       && "Social Media Analytics"}
              {activeTab === "audience"     && "Audience Intelligence"}
              {activeTab === "demographics" && "Age Demographics"}
              {activeTab === "technology"   && "Technology & Devices"}
              {activeTab === "trending"     && "Trending Stories"}
              {activeTab === "goals"        && "Goals Tracker"}
              {activeTab === "reports"      && "Scheduled Reports"}
              {activeTab === "admin"        && "Admin Panel"}
            </h2>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", position: "relative" }}>
            {/* Live badge — stat indicator */}
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", background: "#22c55e11", border: "1px solid #22c55e33", borderRadius: 20 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e", display: "inline-block", animation: "pulse 2s infinite", flexShrink: 0 }} />
              {gaData.realtime?.activeUsers > 0
                ? <><span style={{ fontSize: 12, fontWeight: 900, color: "#22c55e" }}>{gaData.realtime.activeUsers.toLocaleString()}</span><span style={{ fontSize: 11, color: t.textMuted }}> active</span></>
                : <span style={{ fontSize: 11, fontWeight: 700, color: "#22c55e" }}>Live</span>}
            </div>
            {/* Toolbar — positioned relative so dropdown anchors here */}
            <DashboardToolbar
              dateFilter={dateFilter} setDateFilter={setDateFilter}
              activeTab={activeTab} theme={t} isMobile={isMobile} gaData={gaData}
            />
          </div>
        </div>

        {/* ── GA Date Preset Bar (Task 6) ── */}
        {gaConnected && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 20, flexWrap: "wrap", background: t.surface, border: `1px solid ${t.border}`, borderRadius: 24, padding: "4px 6px", alignSelf: "flex-start", width: "fit-content" }}>
            {[
              { key: "today", label: "Today" },
              { key: "7d",    label: "7 days" },
              { key: "28d",   label: "28 days" },
              { key: "90d",   label: "90 days" },
            ].map(p => (
              <button key={p.key} onClick={() => setGaPreset && setGaPreset(p.key)}
                style={{ background: gaPreset === p.key ? "#2563EB" : "transparent", border: "none", borderRadius: 20, padding: "5px 14px", cursor: "pointer", fontSize: 11, fontWeight: gaPreset === p.key ? 800 : 600, color: gaPreset === p.key ? "#fff" : t.textMuted, transition: "all 0.18s cubic-bezier(.4,0,.2,1)", fontFamily: "Georgia, serif", boxShadow: gaPreset === p.key ? "0 2px 8px #2563EB44" : "none" }}>
                {p.label}
              </button>
            ))}
          </div>
        )}

        {activeTab === "overview" && (
          <div className="section-fade">
            {/* ── Top-level summary cards ── */}
            {(() => {
              const overviewPerPub = gaData.overviewPerPub || {};
              const dailyPerPub    = gaData.dailyViewsPerPub || {};
              const topPubEntry = Object.values(overviewPerPub)
                .filter(p => p.connected !== false)
                .sort((a, b) => (b.totalUsers || 0) - (a.totalUsers || 0))[0] || null;
              const totalWkViews = Object.values(dailyPerPub).reduce((sum, p) => sum + (p.total || 0), 0);
              return (
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)", gap: 14, marginBottom: 24 }}>
                  {[
                    { label: "Page Views", sub: "30-day total", val: gaData.overview?.pageViews ?? null, icon: "📄", color: "#60a5fa", fmt: v => v.toLocaleString() },
                    { label: "Active Now",  sub: "Realtime users", val: gaData.realtime?.activeUsers ?? null, icon: "🟢", color: "#34d399", fmt: v => v.toLocaleString() },
                    { label: "Top Publication", sub: "by total users", val: topPubEntry?.displayName || topPubEntry?.publication || null, icon: "🏆", color: "#fb923c", fmt: v => v },
                    { label: "Weekly Views", sub: "all platforms · real GA4", val: totalWkViews || null, icon: "📊", color: "#a78bfa", fmt: v => v.toLocaleString() },
                  ].map((c, i) => (
                    <div key={i} style={{ background: "#1e2340", borderRadius: 10, padding: "16px 18px", position: "relative", overflow: "hidden" }}>
                      <div style={{ fontSize: 18, marginBottom: 6 }}>{c.icon}</div>
                      <div style={{ fontSize: isMobile ? 16 : 22, fontWeight: 900, color: c.val != null ? c.color : "#475569", fontFamily: "Georgia, serif", lineHeight: 1, marginBottom: 4 }}>
                        {c.val == null ? "—" : c.fmt(c.val)}
                      </div>
                      <div style={{ fontSize: 9, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>{c.label}</div>
                      <div style={{ fontSize: 9, color: "#64748b", marginTop: 1 }}>{c.sub}</div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* ── Hourly Readership + Weekly Comparison — real GA4, summed across publications ── */}
            {(() => {
              const dailyPerPub  = Object.values(gaData.dailyViewsPerPub  || {});
              const hourlyPerPub = Object.values(gaData.hourlyViewsPerPub || {});
              const hourlyData = Array.from({ length: 24 }, (_, h) => ({
                hour: h, label: hourLabel(h),
                views: hourlyPerPub.reduce((sum, p) => sum + (p.hourly?.[h] || 0), 0),
              }));
              const dayCount = Math.max(0, ...dailyPerPub.map(p => p.days?.length || 0));
              const weeklyData = Array.from({ length: dayCount }, (_, i) => ({
                day: dailyPerPub.find(p => p.days?.[i])?.days[i]?.dayLabel || "",
                thisWeek: dailyPerPub.reduce((sum, p) => sum + (p.days?.[i]?.views || 0), 0),
                lastWeek: dailyPerPub.reduce((sum, p) => sum + (p.prevDays?.[i]?.views || 0), 0),
              }));
              return (
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 20, marginBottom: 24 }}>
                  <div style={{ background: t.surface, borderRadius: 18, padding: "20px 22px", border: `1px solid ${t.border}` }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: t.text, marginBottom: 4, fontFamily: "Georgia, serif" }}>Hourly Readership — Today</div>
                    <div style={{ fontSize: 10, color: t.textFaint, marginBottom: 12 }}>All publications · page views aggregated</div>
                    <HourlyReadershipChart hourlyData={hourlyData} color="#2563EB" height={170} />
                  </div>
                  <div style={{ background: t.surface, borderRadius: 18, padding: "20px 22px", border: `1px solid ${t.border}` }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: t.text, marginBottom: 4, fontFamily: "Georgia, serif" }}>Views This Week vs Last Week</div>
                    <div style={{ display: "flex", gap: 14, marginBottom: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <div style={{ width: 18, height: 2.5, background: "#2563EB", borderRadius: 2 }} />
                        <span style={{ fontSize: 9, color: t.textMuted }}>This week</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <svg width="18" height="4"><line x1="0" y1="2" x2="18" y2="2" stroke="#64748b" strokeWidth="2" strokeDasharray="4 3" /></svg>
                        <span style={{ fontSize: 9, color: t.textMuted }}>Last week</span>
                      </div>
                    </div>
                    <WeeklyCompareChart weeklyData={weeklyData} color="#2563EB" height={170} />
                  </div>
                </div>
              );
            })()}

            {/* ── GA4 KPI cards with delta badges ── */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap: 14, marginBottom: 24 }}>
              {[
                { label: "New Users",             value: gaData.overview?.newUsers           ?? null, change: gaData.overview?.newUsersChange     ?? null, color: "#2563EB", icon: "👤", fmt: v => v.toLocaleString() },
                { label: "Engaged Users",          value: gaData.overview?.activeUsers        ?? null, change: gaData.overview?.activeUsersChange   ?? null, color: "#059669", icon: "👥", fmt: v => v.toLocaleString() },
                { label: "Avg. Session Duration",  value: gaData.overview?.avgSessionDuration ?? null, change: null, color: "#0891B2", icon: "⏱", fmt: v => v },
                { label: "Engagement Rate",        value: gaData.overview?.engagementRate     ?? null, change: null, color: "#7C3AED", icon: "📈", fmt: v => v },
              ].map((k, i) => {
                const changePos = k.change !== null && k.change > 0;
                const changeNeg = k.change !== null && k.change < 0;
                const sparkData = k.value !== null ? makeSparkline(k.value, k.change, 7) : [];
                return (
                  <div key={i} style={{ background: t.name === "dark" ? t.surface : "#f5f3ee", borderRadius: 16, padding: "18px 20px 14px", boxShadow: t.name === "dark" ? "none" : "0 1px 6px #00000010", position: "relative", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: k.color }} />
                    <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 44, opacity: 0.6 }}>
                      {sparkData.length > 0 && <Sparkline data={sparkData} color={k.color} height={44} width={999} />}
                    </div>
                    <div style={{ fontSize: 18, marginBottom: 8 }}>{k.icon}</div>
                    <div style={{ fontSize: isMobile ? 20 : 26, fontWeight: 900, color: k.value === null ? t.textFaint : k.color, fontFamily: "Georgia, serif", lineHeight: 1, marginBottom: 6, fontStyle: k.value === null ? "italic" : "normal" }}>
                      {k.value === null ? "—" : k.fmt(k.value)}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
                      <div style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>{k.label}</div>
                      {k.change !== null && (
                        <span title={`vs. previous ${gaPreset || "period"}`} style={{ fontSize: 9, fontWeight: 800, padding: "1px 5px", borderRadius: 6, cursor: "default", background: changePos ? "#22c55e18" : changeNeg ? "#DC262618" : "#64748b18", color: changePos ? "#22c55e" : changeNeg ? "#DC2626" : "#64748b", border: `1px solid ${changePos ? "#22c55e33" : changeNeg ? "#DC262633" : "#64748b33"}` }}>
                          {changePos ? "▲" : changeNeg ? "▼" : "–"} {Math.abs(k.change)}%
                        </span>
                      )}
                    </div>
                    {k.value !== null && <div style={{ fontSize: 10, color: "#22c55e", fontWeight: 700, position: "relative", zIndex: 1 }}>GA4 · Live</div>}
                    <div style={{ height: 44 }} />{/* spacer for sparkline overlay */}
                  </div>
                );
              })}
            </div>

            {/* ── Total Social Followers summary card ── */}
            <TotalSocialFollowersCard theme={t} isMobile={isMobile} />

            {/* ── Two equal columns: Traffic by Channel | Top Pages ── */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 20, marginBottom: 24 }}>
              {/* Traffic by channel */}
              <div style={{ background: t.surface, borderRadius: 18, padding: "20px 22px", border: `1px solid ${t.border}`, minHeight: 280, display: "flex", flexDirection: "column" }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: t.text, marginBottom: 16, fontFamily: "Georgia, serif" }}>Traffic by channel</div>
                {gaChannels.length > 0 ? (
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                    <DonutChart data={Object.fromEntries(gaChannels.map(ch => [ch.channel, ch.totalUsers]))} colors={gaChannels.map(ch => ch.color)} theme={t} />
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: t.textFaint, fontStyle: "italic", padding: "20px 0" }}>No channel data — connect GA4.</div>
                )}
              </div>

              {/* Top pages */}
              <div style={{ background: t.surface, borderRadius: 18, padding: "20px 22px", border: `1px solid ${t.border}`, minHeight: 280 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: t.text, marginBottom: 14, fontFamily: "Georgia, serif" }}>Top pages (7 days)</div>
                {Array.isArray(gaData.pages) && gaData.pages.length > 0 ? gaData.pages.slice(0, 5).map((a, i) => {
                  const pubColorMap = { newsday: "#2563EB", theindependent: "#7C3AED", zimind: "#7C3AED", standard: "#0891B2", thestandard: "#0891B2", southerneye: "#059669" };
                  const hostKey = (a.hostname || "").toLowerCase().replace(/www\.|\.co\.zw|\.com/g, "");
                  const accentColor = pubColorMap[hostKey] || "#2563EB";
                  return (
                  <div key={i} style={{ borderRadius: 8, transition: "background 0.15s", cursor: "default" }}
                    onMouseEnter={e => e.currentTarget.style.background = t.surface2}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "9px 8px 9px 12px", borderLeft: `3px solid ${accentColor}44`, borderRadius: "0 6px 6px 0" }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: t.text, lineHeight: 1.4, marginBottom: 3, fontFamily: "Georgia, serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.title}</div>
                        <div style={{ fontSize: 10, color: accentColor, fontWeight: 700, opacity: 0.8 }}>{a.hostname}</div>
                      </div>
                      <div style={{ textAlign: "right", flexShrink: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 800, color: accentColor }}>{a.views.toLocaleString()}</div>
                        <div style={{ fontSize: 9, color: t.textFaint, textTransform: "uppercase" }}>Views</div>
                      </div>
                    </div>
                    {i < Math.min(gaData.pages.length, 5) - 1 && <div style={{ height: 1, background: t.border, margin: "0 8px" }} />}
                  </div>);
                }) : (
                  <div style={{ fontSize: 12, color: t.textFaint, fontStyle: "italic" }}>No page data — connect GA4.</div>
                )}
              </div>
            </div>

            {/* ── Acquisition channels + Reader locations ── */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 20, marginBottom: 24 }}>
              <div style={{ background: t.surface, borderRadius: 18, padding: "20px 22px", border: `1px solid ${t.border}` }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: t.text, marginBottom: 14, fontFamily: "Georgia, serif" }}>Acquisition channels</div>
                {gaChannels.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {gaChannels.slice(0, 5).map(ch => (
                      <div key={ch.channel}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <span style={{ fontSize: 12, color: t.textSub }}>{ch.channel}</span>
                          <span style={{ fontSize: 12, fontWeight: 800, color: ch.color }}>{ch.totalUsersPct}%</span>
                        </div>
                        <div style={{ background: t.surface2, borderRadius: 3, height: 4, overflow: "hidden" }}>
                          <div style={{ width: `${ch.totalUsersPct}%`, height: "100%", background: ch.color, borderRadius: 3, transition: "width 1s ease" }} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: t.textFaint, fontStyle: "italic" }}>No channel data — connect GA4.</div>
                )}
              </div>
              <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 18, padding: 20 }}>
                <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 14, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>📍 Reader Locations</div>
                {Object.keys(gaLocations).length > 0
                  ? Object.entries(gaLocations).map(([city, val]) => <MiniBar key={city} label={city} value={`${val}%`} max={100} color="#2563EB" theme={t} />)
                  : <div style={{ fontSize: 12, color: t.textFaint, fontStyle: "italic" }}>No location data — connect GA4.</div>
                }
              </div>
            </div>

            {/* ── AI Trending Stories ── */}
            <TrendingPanel isMobile={isMobile} theme={t} />
          </div>
        )}

        {activeTab === "platforms" && (
          <div className="section-fade">
            {/* Platform overview summary table */}
            <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: 24, overflowX: "auto", marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
                <h3 style={{ margin: 0, fontSize: 14, color: t.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Platform Overview</h3>
                {gaData.overviewPerPub
                  ? <span style={{ fontSize: 10, color: "#22c55e", background: "#22c55e11", padding: "3px 10px", borderRadius: 20, border: "1px solid #22c55e33", fontWeight: 700 }}>● GA4 Live</span>
                  : gaConnected
                    ? <span style={{ fontSize: 10, color: "#f59e0b", background: "#f59e0b11", padding: "3px 10px", borderRadius: 20, border: "1px solid #f59e0b33" }}>Loading per-pub data…</span>
                    : null}
              </div>
              {gaData.overviewPerPub ? (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 720 }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${t.border}` }}>
                      {["Platform", "Total Users", "New Users", "Returning Users", "Sessions", "Avg Duration", "Bounce Rate", "Engagement"].map(h => (
                        <th key={h} style={{ padding: "8px 10px", textAlign: h === "Platform" ? "left" : "right", color: t.textMuted, fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.8, whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { key: "newsday", label: "NewsDay" }, { key: "zimind", label: "ZimInd" },
                      { key: "standard", label: "Standard" }, { key: "southerneye", label: "SouthernEye" },
                    ].map(({ key, label }) => {
                      const d = gaData.overviewPerPub[key] || {}; const color = PLATFORM_COLORS[label];
                      const disconnected = d.connected === false;
                      return (
                        <tr key={key} style={{ borderBottom: `1px solid ${t.border}`, transition: "background 0.15s" }}
                          onMouseEnter={e => e.currentTarget.style.background = t.surface2}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <td style={{ padding: "11px 10px" }}><div style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ width: 8, height: 8, borderRadius: 2, background: color }} /><span style={{ color: t.text, fontWeight: 700 }}>{label}</span>{disconnected && <span style={{ fontSize: 9, color: "#94a3b8", background: "#94a3b811", padding: "1px 6px", borderRadius: 10, border: "1px solid #94a3b833", marginLeft: 4 }}>Not connected</span>}</div></td>
                          <td style={{ padding: "11px 10px", textAlign: "right", color: disconnected ? t.textFaint : color, fontWeight: 800 }}>{(d.totalUsers || 0).toLocaleString()}</td>
                          <td style={{ padding: "11px 10px", textAlign: "right", color: t.textSub }}>{(d.newUsers || 0).toLocaleString()}</td>
                          <td style={{ padding: "11px 10px", textAlign: "right", color: t.textSub }}>{(d.returningUsers || 0).toLocaleString()}</td>
                          <td style={{ padding: "11px 10px", textAlign: "right", color: t.textSub }}>{(d.sessions || 0).toLocaleString()}</td>
                          <td style={{ padding: "11px 10px", textAlign: "right", color: t.textSub }}>{d.avgSessionDuration || "—"}</td>
                          <td style={{ padding: "11px 10px", textAlign: "right", color: t.textSub }}>{d.bounceRate || "—"}</td>
                          <td style={{ padding: "11px 10px", textAlign: "right", color: disconnected ? t.textFaint : "#22c55e", fontWeight: disconnected ? 400 : 700 }}>{d.engagementRate || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div style={{ padding: "32px 0", textAlign: "center", color: t.textFaint, fontSize: 13, fontStyle: "italic" }}>
                  {gaConnected ? "Loading GA4 data…" : "Connect GA4 to see per-publication data."}
                </div>
              )}
            </div>

            {/* Active Users — Last 30 Minutes */}
            {gaData.realtimePerPub && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                  <div style={{ width: 4, height: 22, background: "linear-gradient(180deg,#22c55e,#059669)", borderRadius: 2 }} />
                  <h3 style={{ margin: 0, fontSize: 13, color: t.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Active Users — Last 30 Minutes</h3>
                  <span style={{ fontSize: 9, color: "#22c55e", background: "#22c55e11", padding: "2px 8px", borderRadius: 20, border: "1px solid #22c55e33", fontWeight: 700 }}>Live</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)", gap: 16 }}>
                  {[
                    { key: "newsday", label: "NewsDay", color: "#2563EB" },
                    { key: "zimind", label: "ZimInd", color: "#7C3AED" },
                    { key: "standard", label: "Standard", color: "#0891B2" },
                    { key: "southerneye", label: "SouthernEye", color: "#059669" },
                  ].map(pub => {
                    const d = gaData.realtimePerPub[pub.key];
                    const activeNow = d?.activeNow || 0;
                    const perMinute = d?.perMinute || Array(30).fill(0);
                    return (
                      <div key={pub.key} style={{ background: t.surface, border: `1px solid ${pub.color}33`, borderRadius: 16, padding: 18 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
                          <div style={{ width: 8, height: 8, borderRadius: "50%", background: pub.color }} />
                          <span style={{ fontSize: 12, fontWeight: 800, color: t.text }}>{pub.label}</span>
                        </div>
                        <div style={{ fontSize: 28, fontWeight: 900, color: pub.color, fontFamily: "Georgia, serif", lineHeight: 1, marginBottom: 4 }}>
                          {activeNow.toLocaleString()}
                        </div>
                        <div style={{ fontSize: 9, color: t.textFaint, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 10 }}>Active now</div>
                        <Sparkline data={perMinute} color={pub.color} height={32} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2,1fr)" : "repeat(3,1fr)", gap: 22, marginBottom: socialData?.publications?.length ? 28 : 0 }}>
              {PLATFORMS.filter(p => p !== "Hstv").map(p => {
                const key = p.toLowerCase();
                return <PlatformCard key={p} platform={p}
                  gaOverviewPerPub={gaData.overviewPerPub?.[key] || null}
                  gaAudiencePerPub={gaData.audiencePerPub?.[key] || null}
                  gaRealtimePerPub={gaData.realtimePerPub?.[key] || null}
                  gaChannelsPerPub={gaData.channelsPerPub?.[key] || null}
                  gaDailyViewsPerPub={gaData.dailyViewsPerPub?.[key] || null}
                  gaHourlyViewsPerPub={gaData.hourlyViewsPerPub?.[key] || null}
                  perPubLoaded={!!gaData.overviewPerPub}
                  theme={t} />;
              })}
            </div>
            {socialData?.publications?.length > 0 && (
              <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 20, padding: isMobile ? 16 : 22 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                  <div style={{ width: 4, height: 20, background: "linear-gradient(180deg, #2563EB, #D97706)", borderRadius: 2 }} />
                  <h3 style={{ margin: 0, fontSize: 12, color: t.textSub, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 }}>Social Media Followers by Publication</h3>
                  <span style={{ fontSize: 9, color: "#22c55e", background: "#22c55e11", padding: "2px 8px", borderRadius: 20, border: "1px solid #22c55e33", fontWeight: 700 }}>Live</span>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 500 }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${t.border}` }}>
                        {["Publication", "Facebook", "Twitter/X", "Instagram", "Total"].map(h => (
                          <th key={h} style={{ padding: "8px 12px", textAlign: h === "Publication" ? "left" : "right", color: t.textMuted, fontWeight: 700, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.8, whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {socialData.publications.map(pub => {
                        const fb = pub.platforms.facebook; const tw = pub.platforms.twitter;
                        const ig = pub.platforms.instagram;
                        return (
                          <tr key={pub.key} style={{ borderBottom: `1px solid ${t.border}`, transition: "background 0.15s" }}
                            onMouseEnter={e => e.currentTarget.style.background = t.surface2}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            <td style={{ padding: "10px 12px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                <div style={{ width: 8, height: 8, borderRadius: "50%", background: pub.color }} />
                                <span style={{ color: t.text, fontWeight: 700 }}>{pub.name}</span>
                              </div>
                            </td>
                            <td style={{ padding: "10px 12px", textAlign: "right", color: fb?.configured ? "#1877F2" : t.textFaint, fontWeight: fb?.configured ? 800 : 400 }}>
                              {fb?.configured ? (fb.followers ?? 0).toLocaleString() : "—"}
                            </td>
                            <td style={{ padding: "10px 12px", textAlign: "right", color: tw?.configured ? "#1DA1F2" : t.textFaint, fontWeight: tw?.configured ? 800 : 400 }}>
                              {tw?.configured ? (tw.followers ?? 0).toLocaleString() : "—"}
                            </td>
                            <td style={{ padding: "10px 12px", textAlign: "right", color: ig?.configured ? "#E1306C" : t.textFaint, fontWeight: ig?.configured ? 800 : 400 }}>
                              {ig?.configured ? (ig.followers ?? 0).toLocaleString() : "—"}
                            </td>
                            <td style={{ padding: "10px 12px", textAlign: "right", color: pub.totalFollowers > 0 ? pub.color : t.textFaint, fontWeight: 900 }}>
                              {pub.totalFollowers > 0 ? pub.totalFollowers.toLocaleString() : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "social" && (
          <SocialOverviewGrid
            socialData={socialData}
            socialRealtime={socialRealtime}
            twitterByPub={twitterByPub}
            loading={socialLoading}
            error={socialError}
            isMobile={isMobile}
            isTablet={isTablet}
            theme={t}
          />
        )}

        {activeTab === "audience" && (
          <AudienceTab
            gaData={gaData} gaConnected={gaConnected} gaChannels={gaChannels}
            gaChannelTotals={gaChannelTotals} gaLocations={gaLocations}
            isMobile={isMobile} isTablet={isTablet} theme={t}
          />
        )}

        {activeTab === "demographics" && (
          <div className="section-fade">
            <AgeDemographicsPanel isMobile={isMobile} theme={t} gaData={gaData} gaConnected={gaConnected} />
          </div>
        )}

        {activeTab === "technology" && (
          <TechnologyTab gaData={gaData} gaConnected={gaConnected} isMobile={isMobile} isTablet={isTablet} theme={t} />
        )}
        {activeTab === "trending" && (
          <div className="section-fade" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <TrendingPanel isMobile={isMobile} theme={t} />
            <TrendingStoriesPanel isMobile={isMobile} theme={t} pages={gaData.pages || []} />
          </div>
        )}
        {activeTab === "goals"   && <GoalsPage   theme={t} isMobile={isMobile} user={user} />}
        {activeTab === "reports" && <ReportsPage theme={t} isMobile={isMobile} user={user} />}
        {activeTab === "admin"   && <AdminSection user={user} theme={t} isMobile={isMobile} />}

        {/* No-access gate for viewers with restricted permissions */}
        {activeTab !== "admin" && !permittedKeys.has(activeTab) && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 14, textAlign: "center" }}>
            <div style={{ fontSize: 48, opacity: 0.4 }}>🔒</div>
            <div style={{ fontSize: 18, fontWeight: 900, color: t.text, fontFamily: "Georgia, serif" }}>Access Restricted</div>
            <div style={{ fontSize: 13, color: t.textMuted, maxWidth: 360 }}>You don't have access to this section. Contact your administrator to request access.</div>
          </div>
        )}

        </div>

        {showShare && <ShareModal theme={t} activeTab={activeTab} onClose={() => setShowShare(false)} />}
      </div>{/* end main-content wrapper */}

      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #334155; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: #475569; }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
      `}</style>
    </div>
  );
}

// ── App Root ──────────────────────────────────────────────────────────────────
const GA_PRESET_MAP = {
  today: { start: "today",      end: "today" },
  "7d":  { start: "7daysAgo",  end: "today" },
  "28d": { start: "28daysAgo", end: "today" },
  "90d": { start: "90daysAgo", end: "today" },
};

function AppInner() {
  const [user, setUser] = useState(null);
  const [isDark, setIsDark] = useState(() => {
    try { const s = localStorage.getItem("amh-theme"); return s ? s === "dark" : true; } catch { return true; }
  });
  const [gaPreset, setGaPreset] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("range") || "7d"; } catch { return "7d"; }
  });

  const gaDateParams = useMemo(() => GA_PRESET_MAP[gaPreset] || {}, [gaPreset]);
  const { data: gaData, connected: gaConnected } = useAnalytics(gaDateParams);

  // Sync data-theme on HTML element for CSS custom properties
  useEffect(() => {
    try { document.documentElement.setAttribute("data-theme", isDark ? "dark" : "light"); } catch {}
  }, [isDark]);

  // Persist range in URL
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      params.set("range", gaPreset);
      window.history.replaceState(null, "", `${window.location.pathname}?${params}`);
    } catch {}
  }, [gaPreset]);

  // Load user theme preference after login
  const handleLogin = (u) => {
    setUser(u);
    if (u?.preferences?.theme) {
      const dark = u.preferences.theme === "dark";
      setIsDark(dark);
      try { localStorage.setItem("amh-theme", u.preferences.theme); } catch {}
    }
  };

  // Force back to login when any fetchWithAuth call can't refresh
  useEffect(() => {
    const onExpired = () => { setAccessToken(null); setUser(null); };
    window.addEventListener("auth:expired", onExpired);
    return () => window.removeEventListener("auth:expired", onExpired);
  }, []);

  const handleThemeToggle = () => {
    setIsDark(d => {
      const next = !d;
      try { localStorage.setItem("amh-theme", next ? "dark" : "light"); } catch {}
      // Persist to server preference
      fetchWithAuth(`${API_URL}/api/users/preferences`, {
        method: "PUT",
        body: JSON.stringify({ theme: next ? "dark" : "light" }),
      }).catch(() => {});
      return next;
    });
  };

  const handleLogout = async () => {
    try {
      const token = getAccessToken();
      await fetch(`${API_URL}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    } catch {}
    setAccessToken(null);
    setUser(null);
  };

  const theme = isDark ? THEMES.dark : THEMES.light;
  return user
    ? <Dashboard user={user} onLogout={handleLogout} theme={theme} onThemeToggle={handleThemeToggle} gaData={gaData} gaConnected={gaConnected} gaPreset={gaPreset} setGaPreset={setGaPreset} />
    : <LoginScreen onLogin={handleLogin} />;
}

export default function App() {
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/shared/")) {
    return <SharedView />;
  }
  return <AppInner />;
}